import 'reactflow/dist/style.css';
import EventBus from 'eventing-bus';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Close as CloseIcon, ContentCopy, DataObject, Code, CheckCircle, Error } from '@mui/icons-material';
import ReactFlow, { ReactFlowProvider, Controls, Background, applyNodeChanges, applyEdgeChanges, addEdge, MarkerType, useReactFlow, Panel, BackgroundVariant } from 'reactflow';
import { Box, Paper, IconButton, Tooltip, Drawer, Typography, TextField, Chip, Alert, Table, TableBody, TableCell, TableRow, TableContainer, CircularProgress } from '@mui/material';

import constants from '../../../constants';
import { getAllPhones } from '../../../store/Phone';
import { getAllAgents } from '../../../store/Agent';
import { getNodeTools } from '../../../store/Integration';
import { executeWorkflow } from '../../../store/Workflows';
import websocketService from '../../../services/websocket.service';
import { useCollaboration } from '../../../components/CollaborativePresence';
import VariableReferenceHelper, { useVariableReferenceHelper } from './components/VariableReferenceHelper';

// Custom node components
import CustomEdge from './components/CustomEdge';
import WorkflowNode from './components/WorkflowNode';
import NodeLibrary from './components/NodeLibrary';
import { getNodeDefinitionService } from './nodeService';
import FieldRenderer from './components/FieldRenderer';
import { createNodeInstance, workflowNodeService } from './nodeService';

// Node types are loaded dynamically from the database
// This will be built from the database node definitions
// Define custom edge types
const edgeTypes = {
  default: (props) => <CustomEdge {...props} />,
  smoothstep: (props) => <CustomEdge {...props} />,
  conditional: (props) => <CustomEdge {...props} />,
}

// Edge styles
const defaultEdgeOptions = {
  type: 'smoothstep',
  style: { strokeWidth: 2, stroke: '#b1b1b7' },
  markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20, color: '#b1b1b7' }
}

// Get success message for nodes without data output
const getSuccessMessage = (nodeType, nodeTemplates = {}, config = {}) => {
  // Update WorkflowNodeService with current templates
  workflowNodeService.updateNodeTemplates(nodeTemplates);

  // Use WorkflowNodeService to get the success message
  return workflowNodeService.getNodeExecutionMessage(nodeType, config, false);
}


const NodeConfigDialog = ({ open, onClose, node, onSave, nodes = [], edges = [], workflowId = null, nodeTemplates = {}, nodeTools = {}, onNavigatePrevious, onNavigateNext }) => {
  if (!node) {
    return null;
  }
  const dispatch = useDispatch();
  const [config, setConfig] = useState({});
  // Note: contactProperties is kept for backward compatibility with FieldRenderer
  // but it's now populated dynamically by the renderer itself based on node's dynamicData config
  const [contactProperties] = useState([]);

  const [isResizing, setIsResizing] = useState(false);
  const [leftPanelWidth, setLeftPanelWidth] = useState(480);
  const [rightPanelWidth, setRightPanelWidth] = useState(480);

  const [isEditingTitle, setIsEditingTitle] = useState(false);

  // Calculate current node position for navigation
  const sortedNodes = [...nodes].sort((a, b) => a.position.x - b.position.x);
  const currentNodeIndex = sortedNodes.findIndex(n => n.id === node.id);
  const currentPosition = currentNodeIndex + 1;
  const totalNodes = nodes.length;
  const isFirstNode = currentNodeIndex === 0;
  const isLastNode = currentNodeIndex === sortedNodes.length - 1;

  // Get previous and next node objects
  const previousNode = currentNodeIndex > 0 ? sortedNodes[currentNodeIndex - 1] : null;
  const nextNode = currentNodeIndex < sortedNodes.length - 1 ? sortedNodes[currentNodeIndex + 1] : null;
  const onPopupToggle = useCallback((next) => {
    setOnPopupToggle(next);
  }, []);

  // Helper to extract node icon SVG
  const getNodeIconSvg = (nodeObj) => {
    if (!nodeObj) return null;

    // Primary source: iconSvg from node data
    if (nodeObj.data?.iconSvg) {
      return nodeObj.data.iconSvg;
    }

    // Fallback: Try to get from node definition
    if (nodeObj.data?.nodeDefinition?.style?.iconSvg) {
      return nodeObj.data.nodeDefinition.style.iconSvg;
    }

    return null;
  };

  // Helper to get node background color
  const getNodeColor = (nodeObj) => {
    if (!nodeObj) return '#6B7280'; // Default gray

    return nodeObj.data?.nodeDefinition?.style?.iconBackgroundColor ||
           nodeObj.data?.nodeDefinition?.style?.color ||
           '#6B7280';
  };

  // Helper to find the input source node (previous connected node)
  const getInputSourceNode = () => {
    if (!edges || edges.length === 0) return null;

    // Find edges where current node is the target
    const incomingEdges = edges.filter(edge => edge.target === node.id);

    if (incomingEdges.length === 0) return null;

    // Get the source node ID from first incoming edge
    const sourceNodeId = incomingEdges[0].source;

    // Find the source node object
    const sourceNode = nodes.find(n => n.id === sourceNodeId);

    return sourceNode;
  };

  // Use the helper function
  const inputSourceNode = getInputSourceNode();
  const hasInputNode = inputSourceNode !== null;
  const hasInputData = inputSourceNode?.data?.output !== undefined && inputSourceNode?.data?.output !== null;
  const inputNodeExecutionStatus = inputSourceNode?.data?.executionStatus;

  // Variable reference helper
  const variableHelper = useVariableReferenceHelper();

  // Helper function to extract properties from output
  const extractProperties = (obj, prefix = '') => {
    const properties = [];
    if (obj === null || obj === undefined) return properties;

    if (typeof obj === 'object') {
      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          const path = prefix ? `${prefix}[${index}]` : `[${index}]`;
          properties.push({ path, value: item, type: typeof item });

          if (typeof item === 'object' && item !== null) properties.push(...extractProperties(item, path));
        });
      } else {
        Object.entries(obj).forEach(([key, value]) => {
          const path = prefix ? `${prefix}.${key}` : key;
          properties.push({ path, value: value, type: typeof value });

          if (typeof value === 'object' && value !== null) properties.push(...extractProperties(value, path));
        });
      }
    }
    return properties;
  }

  // Render node output with clickable property paths
  const renderNodeOutput = (value, nodeId, path = '') => {
    if (value === null) return <span style={{ color: '#999' }}>null</span>;
    if (value === undefined) return <span style={{ color: '#999' }}>undefined</span>;

    const type = typeof value;
    if (type === 'string') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: '#4caf50' }}> "{value}" </Typography>
          <Tooltip title="Copy variable reference">
            <IconButton
              size="small"
              onClick={() => {
                const fullPath = `{{${nodeId}${path ? '.' + path : ''}}}`;
                navigator.clipboard.writeText(fullPath);
                EventBus.publish('success', `Copied: ${fullPath}`);
              }}
              sx={{ padding: '2px' }}
            >
              <ContentCopy sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    if (type === 'number' || type === 'boolean') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: '#2196f3' }}>
            {String(value)}
          </Typography>
          <Tooltip title="Copy variable reference">
            <IconButton
              size="small"
              onClick={() => {
                const fullPath = `{{${nodeId}${path ? '.' + path : ''}}}`;
                navigator.clipboard.writeText(fullPath);
                EventBus.publish('success', `Copied: ${fullPath}`);
              }}
              sx={{ padding: '2px' }}
            >
              <ContentCopy sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    if (Array.isArray(value)) {
      return (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip label={`Array (${value.length} items)`} size="small" icon={<DataObject />} sx={{ height: 20 }} />
            <Tooltip title="Copy variable reference">
              <IconButton
                size="small"
                sx={{ padding: '2px' }}
                onClick={() => {
                  const fullPath = `{{${nodeId}${path ? '.' + path : ''}}}`;
                  navigator.clipboard.writeText(fullPath);
                  EventBus.publish('success', `Copied: ${fullPath}`);
                }}
              >
                <ContentCopy sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ pl: 2 }}>
            {value.map((item, index) => (
              <Box key={index} sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ color: '#666' }}> [{index}]: </Typography>
                {renderNodeOutput(item, nodeId, path ? `${path}[${index}]` : `[${index}]`)}
              </Box>
            ))}
          </Box>
        </Box>
      );
    }

    if (type === 'object') {
      const entries = Object.entries(value);
      return (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip
              label={`Object (${entries.length} properties)`}
              size="small"
              icon={<Code />}
              sx={{ height: 20 }}
            />
            <Tooltip title="Copy variable reference">
              <IconButton
                size="small"
                onClick={() => {
                  const fullPath = `{{${nodeId}${path ? '.' + path : ''}}}`;
                  navigator.clipboard.writeText(fullPath);
                  EventBus.publish('success', `Copied: ${fullPath}`);
                }}
                sx={{ padding: '2px' }}
              >
                <ContentCopy sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableBody>
                {entries.map(([key, val]) => (
                  <TableRow key={key}>
                    <TableCell sx={{ borderBottom: 'none', py: 0.5, color: '#9c27b0' }}>
                      {key}:
                    </TableCell>
                    <TableCell sx={{ borderBottom: 'none', py: 0.5 }}>
                      {renderNodeOutput(val, nodeId, path ? `${path}.${key}` : key)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )
    }

    return <Typography variant="body2">{String(value)}</Typography>;
  }

  // Note: Dynamic data fetching is now handled by FieldRenderer
  // based on the dynamicData configuration in node definitions
  // This makes the system fully dynamic without hardcoded logic

  // Fetch agents for call channel
  const fetchAgents = () => dispatch(getAllAgents())

  // Fetch phones for call channel
  const fetchPhones = () => dispatch(getAllPhones())

  // Fetch agents and phones when dialog opens for communication node with call channel
  useEffect(() => {
    // Check both current config and node's saved config
    const isCallChannel = config['channel'] === 'call' || (node?.['data']?.['config']?.['channel'] === 'call' && !config['channel']);

    // Get node definition service to check behaviors
    const nodeDefService = getNodeDefinitionService(nodeTools);
    const requiresCallResources = nodeDefService?.getNodeBehavior(node?.['type'], 'requiresCallResources');

    if (open && requiresCallResources && isCallChannel) {
      fetchAgents();
      fetchPhones();
    }
  }, [open, node?.['type'], config['channel']]);

  // Subscribe to real-time updates for integrations
  useEffect(() => {
    if (!open) return;

    // Handler to refresh integration status
    const handleIntegrationChange = () => {
      // Get node definition service to check behaviors
      const nodeDefService = getNodeDefinitionService(nodeTools);
      const requiresIntegration = nodeDefService?.getNodeBehavior(node?.['type'], 'requiresIntegration');

      if (requiresIntegration?.enabled) {
        // Get integration mapping based on field value
        const fromField = requiresIntegration.fromField || 'service';
        const fieldValue = node?.['data']?.['config']?.[fromField] || config[fromField];
        const integrationMapping = nodeDefService?.getIntegrationMapping(node?.['type'], fieldValue);

        if (integrationMapping?.statusAction)
          dispatch({ type: integrationMapping.statusAction });
      }
    };

    // Get dynamic events from node integration config
    const nodeDefService = getNodeDefinitionService(nodeTools);
    const integrationConfig = nodeDefService?.getIntegrationConfig(node?.['type']) || {};
    const events = integrationConfig.events || [];

    // Generate event listeners dynamically
    const eventListeners = [];
    events.forEach(eventPattern => {
      // Convert pattern like 'GMAIL_STATUS_CHANGE' to 'gmail-connected', 'gmail-disconnected'
      const serviceName = eventPattern.toLowerCase().split('_')[0];
      const connectedEvent = `${serviceName}-connected`;
      const disconnectedEvent = `${serviceName}-disconnected`;

      eventListeners.push(connectedEvent, disconnectedEvent);
    });

    // Add event listeners
    eventListeners.forEach(eventName => { window.addEventListener(eventName, handleIntegrationChange) })

    return () => {
      // Remove event listeners
      eventListeners.forEach(eventName => { window.removeEventListener(eventName, handleIntegrationChange) })
    };
  }, [open, node['type'], config['channel'], config['service'], dispatch]);

  // Update config when node changes or dialog opens
  useEffect(() => {
    if (node?.['data']?.['config']) {
      let loadedConfig = { ...node['data']['config'] };

      // Apply field mappings from node definition
      const nodeDefService = getNodeDefinitionService(nodeTools);
      const fieldMappings = nodeDefService?.getFieldMappings(node['type']) || {};

      // Apply GET operation field mappings for CRM nodes
      if (node['type'] === 'crm' && loadedConfig['operation'] === 'get' && loadedConfig['filters'] && fieldMappings['get']) {
        // Map filter values back to their input fields
        Object.entries(fieldMappings.get).forEach(([uiField, mapping]) => {
          const filterPath = mapping.targetField;
          const filterValue = filterPath.split('.').reduce((obj, key) => obj?.[key], loadedConfig);

          if (filterValue !== undefined) loadedConfig[uiField] = filterValue;
        });
      }

      // Handle SEARCH operation field preservation for CRM nodes
      if (node['type'] === 'crm' && loadedConfig['operation'] === 'search') {
        // Ensure search-related array fields are preserved
        if (node['data']['config']['searchCriteria']) {
          loadedConfig['searchCriteria'] = node['data']['config']['searchCriteria'];
        }
        if (node['data']['config']['searchLogic']) {
          loadedConfig['searchLogic'] = node['data']['config']['searchLogic'];
        }
        if (node['data']['config']['searchSort']) {
          loadedConfig['searchSort'] = node['data']['config']['searchSort'];
        }
        if (node['data']['config']['searchLimit']) {
          loadedConfig['searchLimit'] = node['data']['config']['searchLimit'];
        }
      }

      // Handle UPDATE operation field preservation for CRM nodes
      if (node['type'] === 'crm' && loadedConfig['operation'] === 'update') {
        // Preserve activityFindBy field directly if it exists
        const savedActivityFindBy = node['data']['config']['activityFindBy'];
        if (savedActivityFindBy !== undefined) {
          loadedConfig['activityFindBy'] = savedActivityFindBy;
        }

        // Preserve activity-specific update fields if they exist at top level
        const activityFields = ['activityPhone', 'activityId', 'activityEmail'];
        activityFields.forEach(field => {
          const savedValue = node['data']['config'][field];
          if (savedValue !== undefined) {
            loadedConfig[field] = savedValue;
          }
        });

        // Apply reverse field mappings for UPDATE operation if field mappings exist
        if (fieldMappings['update']) {
          Object.entries(fieldMappings.update).forEach(([uiField, mapping]) => {
            // Check if conditions are met
            let conditionsMet = true;
            if (mapping.condition) {
              Object.entries(mapping.condition).forEach(([condField, condValue]) => {
                if (loadedConfig[condField] !== condValue) {
                  conditionsMet = false;
                }
              });
            }

            if (conditionsMet) {
              // Get value from target field path and map back to UI field
              const targetPath = mapping.targetField.split('.');
              let value = loadedConfig;

              for (const pathPart of targetPath) {
                if (value && typeof value === 'object' && value[pathPart] !== undefined) {
                  value = value[pathPart];
                } else {
                  value = undefined;
                  break;
                }
              }

              // Set the value to UI field if it exists and determine activityFindBy
              if (value !== undefined && value !== null && value !== '') {
                loadedConfig[uiField] = value;

                // Auto-determine activityFindBy based on which field has a value
                if (uiField === 'activityId' && !loadedConfig['activityFindBy']) {
                  loadedConfig['activityFindBy'] = 'id';
                } else if (uiField === 'activityPhone' && !loadedConfig['activityFindBy']) {
                  loadedConfig['activityFindBy'] = 'phone';
                } else if (uiField === 'activityEmail' && !loadedConfig['activityFindBy']) {
                  loadedConfig['activityFindBy'] = 'email';
                }
              }
            }
          });
        }

        // Enhanced detection: If activityFindBy is still undefined, detect from filters object
        if (!loadedConfig['activityFindBy'] && loadedConfig['filters']) {
          const filters = loadedConfig['filters'];

          if (filters['_id'] && !loadedConfig['activityId']) {
            loadedConfig['activityFindBy'] = 'id';
            loadedConfig['activityId'] = filters['_id'];
          } else if (filters['phoneNumber'] && !loadedConfig['activityPhone']) {
            loadedConfig['activityFindBy'] = 'phone';
            loadedConfig['activityPhone'] = filters['phoneNumber'];
          } else if (filters['emailSubject'] && !loadedConfig['activityEmail']) {
            loadedConfig['activityFindBy'] = 'email';
            loadedConfig['activityEmail'] = filters['emailSubject'];
          }
        }

        // Final fallback: If this is activity entity and no activityFindBy, default to 'id'
        if (!loadedConfig['activityFindBy'] && loadedConfig['entity'] === 'activity') {
          loadedConfig['activityFindBy'] = 'id';
        }

        // Handle contact update fields - preserve and auto-detect updateBy
        if (loadedConfig['entity'] === 'contact') {
          // Preserve contact update fields directly if they exist
          const contactUpdateFields = ['updateBy', 'updateContactId', 'updateContactEmail', 'updateContactPhone'];
          contactUpdateFields.forEach(field => {
            const savedValue = node['data']['config'][field];
            if (savedValue !== undefined) {
              loadedConfig[field] = savedValue;
            }
          });

          // Auto-detect updateBy if not explicitly set
          if (!loadedConfig['updateBy']) {
            // First, check if any of the specific fields are set
            if (loadedConfig['updateContactEmail']) {
              loadedConfig['updateBy'] = 'email';
            } else if (loadedConfig['updateContactPhone']) {
              loadedConfig['updateBy'] = 'phone';
            } else if (loadedConfig['updateContactId']) {
              loadedConfig['updateBy'] = 'id';
            }
            // If still not set, check filters object
            else if (loadedConfig['filters']) {
              const filters = loadedConfig['filters'];
              if (filters['email']) {
                loadedConfig['updateBy'] = 'email';
                loadedConfig['updateContactEmail'] = filters['email'];
              } else if (filters['phone']) {
                loadedConfig['updateBy'] = 'phone';
                loadedConfig['updateContactPhone'] = filters['phone'];
              } else if (filters['_id'] || filters['id']) {
                loadedConfig['updateBy'] = 'id';
                loadedConfig['updateContactId'] = filters['_id'] || filters['id'];
              }
            }
            // If still not set, check contactIdentifier field
            else if (loadedConfig['contactIdentifier']) {
              const identifier = loadedConfig['contactIdentifier'];
              if (identifier.includes('@')) {
                loadedConfig['updateBy'] = 'email';
                loadedConfig['updateContactEmail'] = identifier;
              } else if (identifier.startsWith('+') || /^\d+$/.test(identifier)) {
                loadedConfig['updateBy'] = 'phone';
                loadedConfig['updateContactPhone'] = identifier;
              } else {
                loadedConfig['updateBy'] = 'id';
                loadedConfig['updateContactId'] = identifier;
              }
            }
            // Final fallback
            else {
              loadedConfig['updateBy'] = 'id';
            }
          }

          // If updateBy is set but the corresponding field is empty, populate it from filters/contactIdentifier
          const updateByFieldName = `updateContact${loadedConfig['updateBy'].charAt(0).toUpperCase() + loadedConfig['updateBy'].slice(1)}`;
          if (!loadedConfig[updateByFieldName]) {
            if (loadedConfig['filters']) {
              const filters = loadedConfig['filters'];
              if (loadedConfig['updateBy'] === 'email' && filters['email']) {
                loadedConfig['updateContactEmail'] = filters['email'];
              } else if (loadedConfig['updateBy'] === 'phone' && filters['phone']) {
                loadedConfig['updateContactPhone'] = filters['phone'];
              } else if (loadedConfig['updateBy'] === 'id' && (filters['_id'] || filters['id'])) {
                loadedConfig['updateContactId'] = filters['_id'] || filters['id'];
              }
            }
            if (!loadedConfig[updateByFieldName] && loadedConfig['contactIdentifier']) {
              loadedConfig[updateByFieldName] = loadedConfig['contactIdentifier'];
            }
          }

        }
      }

      // Handle CREATE operation field preservation for CRM nodes
      if (node['type'] === 'crm' && loadedConfig['operation'] === 'create') {
        // Preserve all deal-related fields if entity is 'deal'
        if (loadedConfig['entity'] === 'deal') {
          const dealFields = ['dealTitle', 'dealAmount', 'dealPipeline', 'dealStage',
                             'dealContactId', 'dealCompanyId', 'dealCloseDate', 'dealDescription'];
          dealFields.forEach(field => {
            const savedValue = node['data']['config'][field];
            if (savedValue !== undefined) {
              loadedConfig[field] = savedValue;
            }
          });
        }
      }

      // For communication nodes, ensure all config fields are preserved
      if (node['type'] === 'communication' && node['data']['config']) {
        loadedConfig = {
          ...node['data']['config'],
          // Explicitly preserve these fields if they exist
          agentId: node['data']['config']['agentId'] || node['data']['agentId'],
          phoneId: node['data']['config']['phoneId'] || node['data']['phoneId'],
        };
      }

      // For trigger nodes, ensure all schedule config fields are preserved
      if (node['type'] === 'trigger' && node['data']['config']) {
        // Map 'type' (backend) to 'triggerType' (UI) field
        const backendType = node['data']['config']['type'] || node['data']['config']['triggerType'] || 'manual';
        loadedConfig = {
          ...node['data']['config'],
          // Use triggerType as the primary field for the UI
          triggerType: backendType,
          // Preserve webhook-related fields
          webhookUrl: node['data']['config']['webhookUrl'],
          authentication: node['data']['config']['authentication'] || 'none',
          authToken: node['data']['config']['authToken'],
          customHeaders: node['data']['config']['customHeaders'],
          // Remove type field to avoid confusion - it will be added back on save
          scheduleType: node['data']['config']['scheduleType'],
          interval: node['data']['config']['interval'],
          unit: node['data']['config']['unit'],
          hour: node['data']['config']['hour'],
          minute: node['data']['config']['minute'],
          dayOfWeek: node['data']['config']['dayOfWeek'],
          dayOfMonth: node['data']['config']['dayOfMonth'],
          cronExpression: node['data']['config']['cronExpression'],
          delay: node['data']['config']['delay'],
        };
        // Remove the type field to ensure UI uses triggerType
        delete loadedConfig['type'];
      }

      setConfig(loadedConfig);

      // If it's a communication node with call channel and has saved agent/phone, trigger fetch
      if (node['type'] === 'communication' && loadedConfig['channel'] === 'call' && open) {
        fetchAgents();
        fetchPhones();
      }
    } else if (node) {
      // Get defaults from node definition service
      const nodeDefService = getNodeDefinitionService(nodeTools);
      const defaultConfig = nodeDefService ? nodeDefService.getDefaultConfig(node['type']) : {};
      setConfig(defaultConfig);
    }
  }, [node, node?.['data']?.['config'], open]);

  const handleSave = () => {
    // Note: Field preservation and mapping logic has been moved to node definitions
    // This allows for dynamic field handling without hardcoded node type checks
    let finalConfig = { ...config };
    // IMPORTANT: Explicitly preserve integrationId for communication nodes
    // This field is dynamically injected and must be explicitly preserved
    if (node['type'] === 'communication' && config['integrationId']) {
      finalConfig['integrationId'] = config['integrationId'];
    }

    // Get preserved fields from node definition
    const nodeDefService = getNodeDefinitionService(nodeTools);
    const preservedFields = nodeDefService?.getPreservedFields(node['type']) || [];

    // Ensure preserved fields are included
    preservedFields.forEach(fieldName => {
      if (config[fieldName] !== undefined) finalConfig[fieldName] = config[fieldName];
    });

    // Apply field mappings for CRM GET operation
    if (node['type'] === 'crm' && config['operation'] === 'get') {
      const fieldMappings = nodeDefService?.getFieldMappings(node['type']) || {};


      if (fieldMappings['get']) {
        // Initialize filters object if it doesn't exist
        if (!finalConfig['filters']) {
          finalConfig['filters'] = {};
        }

        Object.entries(fieldMappings.get).forEach(([uiField, mapping]) => {
          // Check if conditions are met
          let conditionsMet = true;
          if (mapping.condition) {
            Object.entries(mapping.condition).forEach(([condField, condValue]) => {
              if (config[condField] !== condValue) {
                conditionsMet = false;
              }
            });
          }

          // If conditions are met and the UI field has a value, map it to the target field
          if (conditionsMet && config[uiField] !== undefined && config[uiField] !== null && config[uiField] !== '') {
            const targetPath = mapping.targetField.split('.');
            let target = finalConfig;

            // Navigate to the target location, creating objects as needed
            for (let i = 0; i < targetPath.length - 1; i++) {
              if (!target[targetPath[i]]) {
                target[targetPath[i]] = {};
              }
              target = target[targetPath[i]];
            }

            // Set the value at the target location
            target[targetPath[targetPath.length - 1]] = config[uiField];

            // ALSO keep the original field in finalConfig for the CRM node to use
            finalConfig[uiField] = config[uiField];
          }
        });
      }
    }

    // Preserve execution data and nodeDefinition when saving config
    const newData = {
      ...node['data'],
      config: finalConfig,
      label: config['label'] || node['data']?.['label'],
      // Explicitly preserve execution data
      output: node['data']?.['output'],
      raw: node['data']?.['raw'],
      executionStatus: node['data']?.['executionStatus'],
      error: node['data']?.['error'],
      // Explicitly preserve nodeDefinition and iconSvg
      nodeDefinition: node['data']?.['nodeDefinition'],
      iconSvg: node['data']?.['iconSvg']
    };

    onSave(node['id'], newData);
    onClose();
  };

  const handleClose = () => {
    // Reset config when closing without saving
    setConfig({});
    onClose();
  };

  const handleTitleChange = (value) => {
    setConfig({ ...config, label: value });
  };

  const handleMouseDown = (e) => {
    e.preventDefault(); // Prevent text selection start
    setIsResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.classList.add('dragging'); // optional CSS class
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      e.preventDefault();
      const deltaX = e.movementX;
      setLeftPanelWidth((prev) => Math.max(480, prev + deltaX));
      setRightPanelWidth((prev) => Math.max(480, prev - deltaX));
    };
    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.classList.remove('dragging');
    };
    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  if (!node) return null;


  // Check if node has been executed (any node type)
  const hasExecutionData = node?.['data']?.['executionStatus'] !== undefined;
  const hasOutput = node?.['data']?.['output'] !== undefined && node?.['data']?.['output'] !== null;
  const showOutput = hasExecutionData; // Show output section for any executed node

  return (
    <>
      {open && (
        <div className={`main-modal new-user-modal modal-dialog-centered workflow-modal workflow-full-screen`}>
          <div className='backdrop'></div>
          <div className='left-side-nodes'>
            <button
              className='node-btn'
              onClick={(e) => {
                e.stopPropagation();
                if (!isFirstNode && onNavigatePrevious) {
                  onNavigatePrevious();
                }
              }}
              disabled={isFirstNode}
              style={{
                opacity: isFirstNode ? 0.3 : 1,
                cursor: isFirstNode ? 'not-allowed' : 'pointer',
                backgroundColor: previousNode ? getNodeColor(previousNode) : '#f5f5f5',
              }}
              title={
                isFirstNode
                  ? 'No previous node'
                  : `Previous: ${previousNode?.data?.label || previousNode?.data?.name || 'Node'}`
              }
            >
              {previousNode && getNodeIconSvg(previousNode) ? (
                <div
                  className="node-icon-preview"
                  dangerouslySetInnerHTML={{ __html: getNodeIconSvg(previousNode) }}
                  style={{
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                />
              ) : (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 5L7 10L12 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          </div>
          <div className='header-area'>
            <button className='backbuton' onClick={() => { handleClose(); }}>
              <i className='icon'>
                <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 13L1 7L7 1" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>
              </i>
              Back to Canvas
            </button>
          </div>
          <div className='content-area'>
            <div className='form-area workflow-full-form' style={{ display: 'flex', height: '100%' }}>
              <div className='input-area-left' style={{ width: leftPanelWidth, minWidth: 480, transition: isResizing ? 'none' : 'width 0.2s' }}>
                <div className='title-area'>
                  <h4>Input</h4>
                  <div className='right-area'>
                    <div className='search-area'>
                      <button className='icon-search'><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.40156 10.6464C2.74292 10.6464 0.578369 8.48183 0.578369 5.82319C0.578369 3.16455 2.74292 1 5.40156 1C8.0602 1 10.2248 3.16455 10.2248 5.82319C10.2248 8.48183 8.0602 10.6464 5.40156 10.6464ZM5.40156 1.70583C3.12878 1.70583 1.2842 3.55511 1.2842 5.82319C1.2842 8.09127 3.12878 9.94055 5.40156 9.94055C7.67434 9.94055 9.51892 8.09127 9.51892 5.82319C9.51892 3.55511 7.67434 1.70583 5.40156 1.70583Z" fill="#50525870" stroke="#50525870" stroke-width="0.236842"></path><path d="M12.8959 13.7066C12.8021 13.7068 12.7083 13.6723 12.6342 13.5984L8.68189 9.63155C8.5386 9.48864 8.53829 9.2518 8.68119 9.10852C8.82409 8.96524 9.06094 8.96492 9.20422 9.10783L13.1565 13.0747C13.2998 13.2176 13.3001 13.4545 13.1572 13.5977C13.0833 13.6718 12.9896 13.7065 12.8959 13.7066Z" fill="#50525870" stroke="#50525870" stroke-width="0.236842"></path></svg></button>
                    </div>
                    <div className='tabs-area'>
                      <button className='tabs-btn'>Schema</button>
                      <button className='tabs-btn active'>Table</button>
                      <button className='tabs-btn'>JSON</button>
                    </div>
                  </div>
                </div>
                <div className='input-area'>
                  {/* Left side - Input from previous node */}
                  {hasInputNode ? (
                    <>
                      <div className=''>
                        <Box sx={{ flex: '1 1 50%', marginTop: '15px' }}>
                          {/* Error Display */}
                          {inputSourceNode?.['data']?.['executionStatus'] === 'failed' && inputSourceNode?.['data']?.['error'] && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                Execution Failed
                              </Typography>
                              <Typography variant="body2">
                                {typeof inputSourceNode['data']['error'] === 'string' ? inputSourceNode['data']['error'] :
                                  inputSourceNode['data']['error']['message'] || 'An error occurred during execution'}
                              </Typography>
                            </Alert>
                          )}

                          {/* Output Display */}
                          <Paper>
                            {hasInputData ? (
                              // Input source node has output data - show it
                              <div className='outp-data-box'>
                                <div className='header-area'>
                                  <h5>
                                    {/* <i className='icon'>
                                      <CheckCircle sx={{ color: '#4caf50', fontSize: 14 }} />
                                    </i> */}
                                    {/* Node Output */}
                                  </h5>
                                  <Chip
                                    label={`{{${inputSourceNode.id}}}`}
                                    size="small"
                                    sx={{ height: 20 }}
                                    onClick={() => {
                                      navigator.clipboard.writeText(`{{${inputSourceNode.id}}}`);
                                      EventBus.publish('success', `Copied: {{${inputSourceNode.id}}}`);
                                    }}
                                  />
                                </div>
                                {/* Render the output with property access */}
                                <div className='output-data'>
                                  {renderNodeOutput(inputSourceNode['data']['output'], inputSourceNode['id'])}
                                </div>
                              </div>
                            ) : (
                              // Input source node has no output data - show execution status
                              <Box>
                                {inputSourceNode['data']['executionStatus'] === 'success' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircle sx={{ color: 'success.main', fontSize: 48 }} />
                                    <Box>
                                      <Typography variant="h6" sx={{ color: 'success.main' }}>
                                        Execution Successful
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                                        {getSuccessMessage(inputSourceNode['type'], nodeTemplates, inputSourceNode['data']?.['config'] || {})}
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                                {inputSourceNode['data']['executionStatus'] === 'error' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Error sx={{ color: 'error.main', fontSize: 48 }} />
                                    <Box>
                                      <Typography variant="h6" sx={{ color: 'error.main' }}>
                                        Execution Failed
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                                        {inputSourceNode['data']['error'] || 'An error occurred during execution'}
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                                {inputSourceNode['data']['executionStatus'] === 'pending' && (
                                  <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                      Node pending execution
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Paper>

                          {/* Available Variables */}
                          <Box sx={{ mt: 2 }}>
                            <h5>Available output variables:</h5>
                            <div className='output-variables'>
                              <Chip
                                icon={<DataObject sx={{ fontSize: 14 }} />}
                                label={`{{${inputSourceNode.id}.output}}`}
                                size="small"
                                sx={{
                                  backgroundColor: 'primary.main',
                                  color: 'white',
                                  '& .MuiChip-icon': { color: 'white' }
                                }}
                                onClick={() => {
                                  navigator.clipboard.writeText(`{{${inputSourceNode.id}.output}}`);
                                  EventBus.publish('success', 'Variable copied to clipboard');
                                }}
                              />
                              {inputSourceNode['data']['raw'] && (
                                <Chip
                                  icon={<Code sx={{ fontSize: 14 }} />}
                                  label={`{{${inputSourceNode.id}.raw}}`}
                                  size="small"
                                  sx={{
                                    backgroundColor: 'grey.600',
                                    color: 'white',
                                    '& .MuiChip-icon': { color: 'white' }
                                  }}
                                  onClick={() => {
                                    navigator.clipboard.writeText(`{{${inputSourceNode.id}.raw}}`);
                                    EventBus.publish('success', 'Variable copied to clipboard');
                                  }}
                                />
                              )}

                              {/* Webhook-specific output variables */}
                              {inputSourceNode['type'] === 'trigger' && inputSourceNode['data']?.['output']?.['webhookData'] && (
                                <>
                                  <Chip
                                    icon={<DataObject sx={{ fontSize: 14 }} />}
                                    label={`{{${inputSourceNode.id}.webhookData.body}}`}
                                    size="small"
                                    sx={{
                                      backgroundColor: 'secondary.main',
                                      color: 'white',
                                      '& .MuiChip-icon': { color: 'white' }
                                    }}
                                    onClick={() => {
                                      navigator.clipboard.writeText(`{{${inputSourceNode.id}.webhookData.body}}`);
                                      EventBus.publish('success', 'Variable copied to clipboard');
                                    }}
                                  />
                                  <Chip
                                    icon={<DataObject sx={{ fontSize: 14 }} />}
                                    label={`{{${inputSourceNode.id}.webhookData.headers}}`}
                                    size="small"
                                    sx={{
                                      backgroundColor: 'secondary.main',
                                      color: 'white',
                                      '& .MuiChip-icon': { color: 'white' }
                                    }}
                                    onClick={() => {
                                      navigator.clipboard.writeText(`{{${inputSourceNode.id}.webhookData.headers}}`);
                                      EventBus.publish('success', 'Variable copied to clipboard');
                                    }}
                                  />
                                  <Chip
                                    icon={<DataObject sx={{ fontSize: 14 }} />}
                                    label={`{{${inputSourceNode.id}.webhookData.query}}`}
                                    size="small"
                                    sx={{
                                      backgroundColor: 'secondary.main',
                                      color: 'white',
                                      '& .MuiChip-icon': { color: 'white' }
                                    }}
                                    onClick={() => {
                                      navigator.clipboard.writeText(`{{${inputSourceNode.id}.webhookData.query}}`);
                                      EventBus.publish('success', 'Variable copied to clipboard');
                                    }}
                                  />
                                  <Chip
                                    icon={<DataObject sx={{ fontSize: 14 }} />}
                                    label={`{{${inputSourceNode.id}.webhookData.method}}`}
                                    size="small"
                                    sx={{
                                      backgroundColor: 'secondary.main',
                                      color: 'white',
                                      '& .MuiChip-icon': { color: 'white' }
                                    }}
                                    onClick={() => {
                                      navigator.clipboard.writeText(`{{${inputSourceNode.id}.webhookData.method}}`);
                                      EventBus.publish('success', 'Variable copied to clipboard');
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </Box>

                          {/* Execution Info */}
                          {inputSourceNode['data']['executionStatus'] && (
                            <div className='status-box'>
                              <p>Status:</p>
                              <Chip
                                label={inputSourceNode.data.executionStatus}
                                size="small"
                                sx={{ ml: 1 }}
                                color={inputSourceNode.data.executionStatus === 'success' ? 'success' : 'default'}
                              />
                            </div>
                          )}
                        </Box>
                      </div>
                    </>
                  ) : (
                    <Box sx={{ flex: '1 1 50%', marginTop: '15px', textAlign: 'center', p: 3 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        No input connection. This is the first node in the workflow.
                      </Typography>
                    </Box>
                  )}
                </div>
              </div>
              <div className='center-form-area'>
                <div
                  className='resizer'
                  style={{
                    cursor: 'grab',
                    zIndex: 10,
                  }}
                  onMouseDown={handleMouseDown}
                >
                  <span className='left-arrow'>
                    <svg class="svg-inline--fa fa-arrow-left fa-w-14" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="arrow-left" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path class="" fill="currentColor" d="M257.5 445.1l-22.2 22.2c-9.4 9.4-24.6 9.4-33.9 0L7 273c-9.4-9.4-9.4-24.6 0-33.9L201.4 44.7c9.4-9.4 24.6-9.4 33.9 0l22.2 22.2c9.5 9.5 9.3 25-.4 34.3L136.6 216H424c13.3 0 24 10.7 24 24v32c0 13.3-10.7 24-24 24H136.6l120.5 114.8c9.8 9.3 10 24.8.4 34.3z"></path></svg>
                  </span>
                  <div className='drag-btn'>
                    <span>...</span>
                  </div>

                  <span className='right-arrow'>
                    <svg class="svg-inline--fa fa-arrow-right fa-w-14" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="arrow-right" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path class="" fill="currentColor" d="M190.5 66.9l22.2-22.2c9.4-9.4 24.6-9.4 33.9 0L441 239c9.4 9.4 9.4 24.6 0 33.9L246.6 467.3c-9.4 9.4-24.6 9.4-33.9 0l-22.2-22.2c-9.5-9.5-9.3-25 .4-34.3L311.4 296H24c-13.3 0-24-10.7-24-24v-32c0-13.3 10.7-24 24-24h287.4L190.9 101.2c-9.8-9.3-10-24.8-.4-34.3z"></path></svg>
                  </span>
                </div>
                <div className='center-form-data'>
                  <div className='title-area'>
                    {isEditingTitle ? (
                      <input
                        autoFocus
                        type='text'
                        className='editable-input'
                        value={config['label'] !== undefined ? config['label'] : (node['data']?.['label'] || '')}
                        onBlur={() => setIsEditingTitle(false)}
                        onChange={(e) => handleTitleChange(e.target.value)}
                      />
                    ) : (
                      <h3 onClick={() => setIsEditingTitle(true)}>
                        <i className='icon'>
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12.5003 18.9582H7.50033C2.97533 18.9582 1.04199 17.0248 1.04199 12.4998V7.49984C1.04199 2.97484 2.97533 1.0415 7.50033 1.0415H12.5003C17.0253 1.0415 18.9587 2.97484 18.9587 7.49984V12.4998C18.9587 17.0248 17.0253 18.9582 12.5003 18.9582ZM7.50033 2.2915C3.65866 2.2915 2.29199 3.65817 2.29199 7.49984V12.4998C2.29199 16.3415 3.65866 17.7082 7.50033 17.7082H12.5003C16.342 17.7082 17.7087 16.3415 17.7087 12.4998V7.49984C17.7087 3.65817 16.342 2.2915 12.5003 2.2915H7.50033Z" fill="white"></path>
                            <path d="M6.10875 12.7C5.97542 12.7 5.84209 12.6583 5.72542 12.5667C5.45042 12.3583 5.40042 11.9667 5.60875 11.6917L7.59209 9.11667C7.83375 8.80834 8.17542 8.60834 8.56709 8.55834C8.95042 8.50834 9.34209 8.61667 9.65042 8.85834L11.1754 10.0583C11.2338 10.1083 11.2921 10.1083 11.3338 10.1C11.3671 10.1 11.4254 10.0833 11.4754 10.0167L13.4004 7.53334C13.6088 7.25834 14.0088 7.20834 14.2754 7.42501C14.5504 7.63334 14.6004 8.02501 14.3838 8.30001L12.4588 10.7833C12.2171 11.0917 11.8754 11.2917 11.4838 11.3333C11.0921 11.3833 10.7088 11.275 10.4004 11.0333L8.87542 9.83334C8.81709 9.78334 8.75042 9.78334 8.71709 9.79167C8.68375 9.79167 8.62542 9.80834 8.57542 9.87501L6.59209 12.45C6.48375 12.6167 6.30042 12.7 6.10875 12.7Z" fill="white"></path>
                          </svg>
                        </i>
                        {config['label'] || node['data']?.['label'] || ''}
                      </h3>
                    )}
                  </div>
                  <div className='left-area'>
                    {/* <div className='group-form'>
                      <label>Label</label>
                      <TextField
                        placeholder="Add label"
                        fullWidth
                        value={config['label'] || node['data']?.['label'] || ''}
                        onChange={(e) => setConfig({ ...config, label: e.target.value })}
                      />
                    </div> */}

                    {/* <div className='group-form'>
                      <label>Description</label>
                      <TextField
                        placeholder="Add description"
                        fullWidth
                        multiline
                        rows={2}
                        value={config['description'] || ''}
                        onChange={(e) => setConfig({ ...config, description: e.target.value })}
                      />
                    </div> */}

                    {/* Dynamic configuration for all node types */}
                    {node['data']?.['nodeDefinition']?.['configFields'] ? (
                      <FieldRenderer
                        configFields={node['data']['nodeDefinition']['configFields']}
                        displayRules={node['data']['nodeDefinition']['displayRules'] || {}}
                        config={config}
                        onChange={setConfig}
                        contactProperties={contactProperties}
                        workflowId={workflowId}
                        nodes={nodes}
                        nodeId={node['id']}
                        nodeTemplate={node['data'] || {}}
                      />
                    ) : (
                      <Alert severity="info">
                        Loading {node['data']?.['label'] || 'node'} configuration...
                      </Alert>
                    )}

                  </div>
                  <div className='btns-box text-right' style={{ width: '100%' }}>
                    <button className='btn-style-border' onClick={handleClose}>Cancel</button>
                    <button className='btn-style-new' onClick={handleSave} >Save</button>
                  </div>
                </div>
              </div>

              <div className='input-area-left input-area-right' style={{ width: rightPanelWidth, minWidth: 480, transition: isResizing ? 'none' : 'width 0.2s' }}>
                <div className='title-area'>
                  <h4>Output</h4>
                  <div className='right-area'>
                    <div className='search-area'>
                      <button className='icon-search'><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5.40156 10.6464C2.74292 10.6464 0.578369 8.48183 0.578369 5.82319C0.578369 3.16455 2.74292 1 5.40156 1C8.0602 1 10.2248 3.16455 10.2248 5.82319C10.2248 8.48183 8.0602 10.6464 5.40156 10.6464ZM5.40156 1.70583C3.12878 1.70583 1.2842 3.55511 1.2842 5.82319C1.2842 8.09127 3.12878 9.94055 5.40156 9.94055C7.67434 9.94055 9.51892 8.09127 9.51892 5.82319C9.51892 3.55511 7.67434 1.70583 5.40156 1.70583Z" fill="#50525870" stroke="#50525870" stroke-width="0.236842"></path><path d="M12.8959 13.7066C12.8021 13.7068 12.7083 13.6723 12.6342 13.5984L8.68189 9.63155C8.5386 9.48864 8.53829 9.2518 8.68119 9.10852C8.82409 8.96524 9.06094 8.96492 9.20422 9.10783L13.1565 13.0747C13.2998 13.2176 13.3001 13.4545 13.1572 13.5977C13.0833 13.6718 12.9896 13.7065 12.8959 13.7066Z" fill="#50525870" stroke="#50525870" stroke-width="0.236842"></path></svg></button>
                    </div>
                    <div className='tabs-area'>
                      <button className='tabs-btn'>Schema</button>
                      <button className='tabs-btn active'>Table</button>
                      <button className='tabs-btn'>JSON</button>
                    </div>
                  </div>
                </div>
                <div className='input-area'>
                  {/* Right side - Output (for all executed nodes) */}
                  {showOutput && (
                    <>
                      <div className=''>
                        {/* <Divider orientation="vertical" flexItem /> */}
                        <Box sx={{ flex: '1 1 50%', marginTop: '15px' }}>
                          {/* <Typography variant="h6" sx={{ fontSize: '1rem', mb: 2 }}>
                            Execution Output
                          </Typography> */}

                          {/* Error Display */}
                          {node?.['data']?.['executionStatus'] === 'failed' && node?.['data']?.['error'] && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                Execution Failed
                              </Typography>
                              <Typography variant="body2">
                                {typeof node['data']['error'] === 'string' ? node['data']['error'] :
                                  node['data']['error']['message'] || 'An error occurred during execution'}
                              </Typography>
                            </Alert>
                          )}

                          {/* Output Display */}
                          <Paper
                          >
                            {hasOutput ? (
                              // Node has output data - show enhanced output display
                              <div className='outp-data-box'>
                                <div className='header-area'>
                                  <h5>
                                    {/* <i className='icon'>
                                      <CheckCircle sx={{ color: '#4caf50', fontSize: 14 }} />
                                    </i> */}
                                    {/* Node Output */}
                                  </h5>
                                  <Chip
                                    label={`{{${node.id}}}`}
                                    size="small"
                                    sx={{ height: 20 }}
                                    onClick={() => {
                                      navigator.clipboard.writeText(`{{${node.id}}}`);
                                      EventBus.publish('success', `Copied: {{${node.id}}}`);
                                    }}
                                  />
                                </div>
                                {/* Render the output with property access */}
                                <div className='output-data'>
                                  {renderNodeOutput(node['data']['output'], node['id'])}
                                </div>
                              </div>
                            ) : (
                              // Node has no output data - show execution status
                              <Box>
                                {node['data']['executionStatus'] === 'success' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircle sx={{ color: 'success.main', fontSize: 48 }} />
                                    <Box>
                                      <Typography variant="h6" sx={{ color: 'success.main' }}>
                                        Execution Successful
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                                        {getSuccessMessage(node['type'], nodeTemplates, node['data']?.['config'] || {})}
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                                {node['data']['executionStatus'] === 'error' && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Error sx={{ color: 'error.main', fontSize: 48 }} />
                                    <Box>
                                      <Typography variant="h6" sx={{ color: 'error.main' }}>
                                        Execution Failed
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                                        {node['data']['error'] || 'An error occurred during execution'}
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                                {node['data']['executionStatus'] === 'pending' && (
                                  <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                      Node pending execution
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Paper>

                          {/* Available Variables */}
                          <Box sx={{ mt: 2 }}>
                            <h5>Available output variables:</h5>
                            <div className='output-variables'>
                              <Chip
                                icon={<DataObject sx={{ fontSize: 14 }} />}
                                label={`{{${node.id}.output}}`}
                                size="small"
                                sx={{
                                  backgroundColor: 'primary.main',
                                  color: 'white',
                                  '& .MuiChip-icon': { color: 'white' }
                                }}
                                onClick={() => {
                                  navigator.clipboard.writeText(`{{${node.id}.output}}`);
                                  EventBus.publish('success', 'Variable copied to clipboard');
                                }}
                              />
                              {node['data']['raw'] && (
                                <Chip
                                  icon={<Code sx={{ fontSize: 14 }} />}
                                  label={`{{${node.id}.raw}}`}
                                  size="small"
                                  sx={{
                                    backgroundColor: 'grey.600',
                                    color: 'white',
                                    '& .MuiChip-icon': { color: 'white' }
                                  }}
                                  onClick={() => {
                                    navigator.clipboard.writeText(`{{${node.id}.raw}}`);
                                    EventBus.publish('success', 'Variable copied to clipboard');
                                  }}
                                />
                              )}

                              {/* Webhook-specific output variables */}
                              {node['type'] === 'trigger' && node['data']?.['output']?.['webhookData'] && (
                                <>
                                  <Chip
                                    icon={<DataObject sx={{ fontSize: 14 }} />}
                                    label={`{{${node.id}.webhookData.body}}`}
                                    size="small"
                                    sx={{
                                      backgroundColor: 'secondary.main',
                                      color: 'white',
                                      '& .MuiChip-icon': { color: 'white' }
                                    }}
                                    onClick={() => {
                                      navigator.clipboard.writeText(`{{${node.id}.webhookData.body}}`);
                                      EventBus.publish('success', 'Variable copied to clipboard');
                                    }}
                                  />
                                  <Chip
                                    icon={<DataObject sx={{ fontSize: 14 }} />}
                                    label={`{{${node.id}.webhookData.headers}}`}
                                    size="small"
                                    sx={{
                                      backgroundColor: 'secondary.main',
                                      color: 'white',
                                      '& .MuiChip-icon': { color: 'white' }
                                    }}
                                    onClick={() => {
                                      navigator.clipboard.writeText(`{{${node.id}.webhookData.headers}}`);
                                      EventBus.publish('success', 'Variable copied to clipboard');
                                    }}
                                  />
                                  <Chip
                                    icon={<DataObject sx={{ fontSize: 14 }} />}
                                    label={`{{${node.id}.webhookData.query}}`}
                                    size="small"
                                    sx={{
                                      backgroundColor: 'secondary.main',
                                      color: 'white',
                                      '& .MuiChip-icon': { color: 'white' }
                                    }}
                                    onClick={() => {
                                      navigator.clipboard.writeText(`{{${node.id}.webhookData.query}}`);
                                      EventBus.publish('success', 'Variable copied to clipboard');
                                    }}
                                  />
                                  <Chip
                                    icon={<DataObject sx={{ fontSize: 14 }} />}
                                    label={`{{${node.id}.webhookData.method}}`}
                                    size="small"
                                    sx={{
                                      backgroundColor: 'secondary.main',
                                      color: 'white',
                                      '& .MuiChip-icon': { color: 'white' }
                                    }}
                                    onClick={() => {
                                      navigator.clipboard.writeText(`{{${node.id}.webhookData.method}}`);
                                      EventBus.publish('success', 'Variable copied to clipboard');
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </Box>

                          {/* Execution Info */}
                          {node['data']['executionStatus'] && (
                            <div className='status-box'>
                              <p>Status:</p>
                              <Chip
                                label={node.data.executionStatus}
                                size="small"
                                sx={{ ml: 1 }}
                                color={node.data.executionStatus === 'success' ? 'success' : 'default'}
                              />
                            </div>
                          )}
                        </Box>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className='right-side-nodes'>
            <button
              className='node-btn'
              onClick={(e) => {
                e.stopPropagation();
                if (!isLastNode && onNavigateNext) {
                  onNavigateNext();
                }
              }}
              disabled={isLastNode}
              style={{
                opacity: isLastNode ? 0.3 : 1,
                cursor: isLastNode ? 'not-allowed' : 'pointer',
                backgroundColor: nextNode ? getNodeColor(nextNode) : '#f5f5f5',
              }}
              title={
                isLastNode
                  ? 'No next node'
                  : `Next: ${nextNode?.data?.label || nextNode?.data?.name || 'Node'}`
              }
            >
              {nextNode && getNodeIconSvg(nextNode) ? (
                <div
                  className="node-icon-preview"
                  dangerouslySetInnerHTML={{ __html: getNodeIconSvg(nextNode) }}
                  style={{
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                />
              ) : (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 5L13 10L8 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Variable Reference Helper */}
      <VariableReferenceHelper
        anchorEl={variableHelper.anchorEl}
        onClose={variableHelper.closeHelper}
        onInsert={variableHelper.handleInsert}
        nodeOutputs={nodes.reduce((acc, n) => {
          if (n.data?.output !== undefined) {
            acc[n.id] = {
              output: n.data.output,
              label: n.data.label || n.type,
              properties: extractProperties(n.data.output)
            };
          }
          return acc;
        }, {})}
      />
    </>
  );
};

const WorkflowBuilderContent = ({ initialNodes = [], initialEdges = [], onSave, onSaveWorkflow, saving = false, saveSuccess = false, isDirty = false, setIsDirty, readOnly = false, workflowId = null, setAdvanceSetting, advanceSetting }) => {
  const reactFlowWrapper = useRef(null);
  const { project } = useReactFlow();

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownOpenSeting, setDropdownOpenSeting] = useState(false);

  const [isActive, setIsActive] = useState(false);
  const [activeType, setActiveType] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [nodeIdCounter, setNodeIdCounter] = useState(1);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [lockedNodes, setLockedNodes] = useState(new Set());
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [isAdvanceSettings, setIsAdvanceSettings] = useState(false);
  const { emitActivity, emitNodeLock, emitChange } = useCollaboration(workflowId);

  // Track when save is successful
  useEffect(() => {
    if (saveSuccess) {
      setIsDirty(false);
      setLastSavedAt(new Date());
    }
  }, [saveSuccess]);

  const handleToggle = (type) => {
    setActiveType((prev) => (prev === type ? null : type));
    // if already open, close; otherwise open clicked one
  };

  // Dynamic node loading state
  // Initialize with WorkflowNode as fallback for all node types
  const [nodeTypes, setNodeTypes] = useState(() => {
    // Create initial nodeTypes with WorkflowNode for common types AND ReactFlow default types
    const commonNodeTypes = [
      'trigger', 'agent', 'communication', 'decision', 'data', 'integration',
      'wait', 'human', 'llm', 'crm',
      // Add ReactFlow's default node types
      'input', 'default', 'output', 'group'
    ];
    const initialTypes = {};
    commonNodeTypes.forEach(type => {
      initialTypes[type] = WorkflowNode;
    });
    // Add generic fallback
    initialTypes.dynamicNode = WorkflowNode;

    // Create a Proxy to handle any unknown node types
    return new Proxy(initialTypes, {
      get: (target, prop) => {
        // If the node type exists, return it
        if (prop in target) {
          return target[prop];
        }
        // Otherwise, return WorkflowNode as fallback
        console.warn(`[ReactFlowBuilder] Unknown node type requested: ${prop}, using WorkflowNode fallback`);
        return WorkflowNode;
      }
    });
  });
  const [nodeTemplates, setNodeTemplates] = useState({}); // Store node metadata/templates
  const [nodeToolsLoaded, setNodeToolsLoaded] = useState(false);
  const [connectionStartHandle, setConnectionStartHandle] = useState(null); // Track connection start handle
  const dispatch = useDispatch();
  const { nodeTools } = useSelector(state => state.Integration);

  // Store functions in refs to avoid circular dependencies
  const deleteNodeRef = useRef();
  const handleExecuteWorkflowRef = useRef();
  const onAddNodeFromEdgeRef = useRef();
  const onAddNodeFromNodeRef = useRef();
  
  // Function to assign sourceHandle values ('true'/'false') to decision node edges
  // This ensures all decision edges have proper sourceHandle for routing and label display
  const assignDecisionEdgeHandles = useCallback((edges, nodes) => {
    
    return edges.map(edge => {
      const sourceNode = nodes.find(n => n.id === edge.source);
      
      // Only assign sourceHandle to decision edges that have null/undefined sourceHandle
      if (sourceNode?.type === 'decision' && (edge.sourceHandle === null || edge.sourceHandle === undefined)) {        
        // Group all edges from this decision node
        const edgesFromDecision = edges.filter(e => e.source === edge.source);
        // Enhanced sorting: First by creation time (edge ID), then by target for consistency
        const sortedEdges = [...edgesFromDecision].sort((a, b) => {
          // Extract numeric part from edge IDs for chronological sorting
          const aNum = parseInt(a.id.replace(/\D/g, '')) || 0;
          const bNum = parseInt(b.id.replace(/\D/g, '')) || 0;
          
          if (aNum !== bNum) {
            return aNum - bNum; // Earlier created edge gets 'true'
          }
          
          // Fallback to target node ID for consistency
          return a.target.localeCompare(b.target);
        });
        
        // Find the index of current edge
        const edgeIndex = sortedEdges.findIndex(e => e.id === edge.id);
        
        // Enhanced logic: Support more than 2 edges, but prioritize first 2
        let sourceHandle;
        if (edgeIndex === 0) {
          sourceHandle = 'true';
        } else if (edgeIndex === 1) {
          sourceHandle = 'false';
        } else {
          // For additional edges beyond true/false, cycle through
          sourceHandle = edgeIndex % 2 === 0 ? 'true' : 'false';
          console.warn(`[ReactFlowBuilder] Decision node ${edge.source} has more than 2 edges. Edge ${edge.id} assigned: ${sourceHandle}`);
        }        
        return {
          ...edge,
          sourceHandle,
          type: 'conditional' // Ensure type is set correctly
        };
      }
      
      // Also ensure existing decision node edges have correct type
      if (sourceNode?.type === 'decision' && edge.sourceHandle && edge.type !== 'conditional') {
        return {
          ...edge,
          type: 'conditional'
        };
      }
      
      return edge;
    });
  }, []);
  
  // Simple onSave wrapper - no automatic edge modifications
  const wrappedOnSave = useCallback((data) => {
    if (onSave) {
      onSave(data);
    }
  }, [onSave]);

  // Delete a specific node by ID
  // Note: This will be properly defined after wrappedOnSave is available
  const deleteNode = useCallback((nodeId) => {
    let updatedNodes = [], updatedEdges = [];

    // Remove the specific node
    setNodes((nds) => {
      updatedNodes = nds.filter((node) => node.id !== nodeId);
      return updatedNodes;
    });

    // Remove edges connected to the deleted node
    setEdges((eds) => {
      updatedEdges = eds.filter((edge) => {
        // Remove if edge is connected to the deleted node
        if (edge.source === nodeId || edge.target === nodeId) return false;
        return true;
      });
      return updatedEdges;
    });

    // Call onSave after deletion
    if (onSave) setTimeout(() => { wrappedOnSave({ nodes: updatedNodes, edges: updatedEdges }) }, 0);

  }, [onSave]);

  // Execute workflow (like Test Run in WorkflowBuilder.js)
  const handleExecuteWorkflow = useCallback(() => {
    // Check if workflowId is available
    if (!workflowId) {
      EventBus.publish('error', 'Please save the workflow first before testing');
      return;
    }

    // Mark all nodes as starting execution
    const startingNodes = nodes.map(node => ({
      ...node,
      data: {
        ...node.data,
        output: null,
        executionStatus: 'pending'
      }
    }));
    setNodes(startingNodes);

    // Execute the workflow
    dispatch(executeWorkflow({
      id: workflowId,
      input: {},
      options: { forceRun: true }, // Allow running draft workflows for testing
      successCallback: (result) => {
        // Don't redirect, show outputs in the builder

        // Extract execution ID and node outputs from the result
        if (result && result['body']) {
          const executionResult = result['body'];
          const executionId = executionResult.executionId || executionResult._id;

          // Build nodeOutputs from executedNodes if not present
          let nodeOutputs = executionResult['nodeOutputs'] || {};

          // If nodeOutputs is empty but executedNodes exists, build it
          if ((!nodeOutputs || Object.keys(nodeOutputs).length === 0) && executionResult['executedNodes']) {
            executionResult['executedNodes'].forEach(executedNode => {
              if (executedNode['nodeId'] && executedNode['output'] !== undefined) {
                nodeOutputs[executedNode['nodeId']] = executedNode['output'];
              }
            });
          }

          // Update nodes with execution results
          const updatedNodes = nodes.map(node => {
            const nodeOutput = nodeOutputs[node['id']];

            // Check if the output indicates an error
            const hasError = nodeOutput && (
              nodeOutput['error'] ||
              (typeof nodeOutput === 'object' && nodeOutput['status'] === 'error')
            );

            if (hasError) {
              return {
                ...node,
                data: {
                  ...node.data,
                  output: null,
                  error: nodeOutput['error'] || nodeOutput['message'] || 'Node execution failed',
                  executionStatus: 'failed'
                }
              };
            } else if (nodeOutput !== undefined) {
              return {
                ...node,
                data: {
                  ...node.data,
                  output: nodeOutput,
                  error: null,
                  executionStatus: 'success'
                }
              };
            }
            return node;
          });

          setNodes(updatedNodes);
        }
      },
      failCallback: (error) => {
        EventBus.publish('error', error?.message || 'Failed to execute workflow');

        // Mark all nodes as failed
        const failedNodes = nodes.map(node => ({
          ...node,
          data: {
            ...node.data,
            executionStatus: 'failed',
            error: error?.message || 'Execution failed'
          }
        }));
        setNodes(failedNodes);
      }
    }));
  }, [workflowId, nodes, dispatch]);

  // Update refs whenever functions change
  deleteNodeRef.current = deleteNode;
  handleExecuteWorkflowRef.current = handleExecuteWorkflow;

  // Update nodes when initialNodes change (e.g., after execution)
  useEffect(() => {
    // Check if initialNodes have execution data that local nodes don't
    const hasExecutionData = initialNodes.some(n => n.data?.output || n.data?.executionStatus);

    if (hasExecutionData && nodes.length > 0) {
      setNodes(prevNodes =>
        prevNodes.map(node => {
          const updatedNode = initialNodes.find(n => n.id === node.id);

          if (updatedNode && (updatedNode.data?.output || updatedNode.data?.executionStatus)) {
            return {
              ...node,
              data: {
                ...node.data,
                output: updatedNode.data.output,
                raw: updatedNode.data.raw,
                executionStatus: updatedNode.data.executionStatus,
                error: updatedNode.data.error,
                // Preserve nodeDefinition from updatedNode if present
                nodeDefinition: updatedNode.data?.nodeDefinition || node.data?.nodeDefinition,
                iconSvg: updatedNode.data?.iconSvg || node.data?.iconSvg
              }
            };
          }
          return node;
        })
      );
    } else if (initialNodes.length > 0 && nodes.length === 0) {
      // Initial load when no nodes exist yet
      // Remove the nodeToolsLoaded condition - nodes should be set regardless
      setNodes(initialNodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          onDelete: () => deleteNodeRef.current?.(node.id),
          onExecute: () => handleExecuteWorkflowRef.current?.(),
          nodeTemplates: nodeTemplates,
          onAddNodeFromNode: (nodeId, nodeType, customConfig) => onAddNodeFromNodeRef.current?.(nodeId, nodeType, customConfig)
        }
      })));
      
      const enrichedEdges = assignDecisionEdgeHandles(initialEdges, initialNodes);
      
      setEdges(enrichedEdges.map(edge => {
        // Check if the source node is a decision node
        const sourceNode = initialNodes.find(n => n.id === edge.source);
        const isDecisionNode = sourceNode?.type === 'decision';
        
        // CRITICAL FIX: Manually assign sourceHandle if still missing for decision nodes
        let finalEdge = { ...edge };
        if (isDecisionNode && !finalEdge.sourceHandle) {
          // Find all decision edges to determine order
          const decisionEdges = enrichedEdges.filter(e => e.source === edge.source);
          const sortedEdges = [...decisionEdges].sort((a, b) => a.target.localeCompare(b.target));
          const edgeIndex = sortedEdges.findIndex(e => e.id === edge.id);
          
          finalEdge.sourceHandle = edgeIndex === 0 ? 'true' : 'false';
        }
        
        // ENHANCED: Force type and sourceHandle for decision edges
        const processedEdge = {
          ...finalEdge,
          type: finalEdge.type || (isDecisionNode ? 'conditional' : 'smoothstep'),
          data: {
            ...finalEdge.data,
            // Add minimal data, full data will be added by effects when nodeTemplates load
            onDelete: (edgeId) => deleteEdgeRef.current(edgeId)
          }
        };
        return processedEdge;
      }));
    }
  }, [initialNodes, initialEdges, nodeTemplates, assignDecisionEdgeHandles]);

  // Update nodeIdCounter when nodes are initialized
  useEffect(() => {
    if (nodes.length > 0) {
      const maxId = Math.max(...nodes.map(n => parseInt(n.id.split('_')[1]) || 0), 0);
      setNodeIdCounter(maxId + 1);
    }
  }, [nodes.length]);

  // Enrich nodes with nodeDefinition when nodeTemplates are loaded
  useEffect(() => {
    if (nodeToolsLoaded && Object.keys(nodeTemplates).length > 0 && nodes.length > 0) {
      // Update WorkflowNodeService with current templates
      workflowNodeService.updateNodeTemplates(nodeTemplates);

      setNodes(prevNodes => prevNodes.map(node => {
        // Process nodes that don't have nodeDefinition yet
        if (!node.data?.nodeDefinition && nodeTemplates[node.type]) {
          const template = nodeTemplates[node.type];

          // Use WorkflowNodeService to process node for loading
          const processedNode = workflowNodeService.processNodeForLoad(
            {
              ...node,
              data: {
                ...node.data,
                nodeDefinition: template.template?.nodeDefinition || null,
                iconSvg: node.data?.iconSvg || template.template?.iconSvg || null
              }
            },
            node.data?.config || {}
          );

          // Add callbacks and nodeTemplates to node data
          return {
            ...processedNode,
            data: {
              ...processedNode.data,
              onDelete: () => deleteNodeRef.current?.(processedNode.id),
              onExecute: () => handleExecuteWorkflowRef.current?.(),
              nodeTemplates: nodeTemplates,
              onAddNodeFromNode: (nodeId, nodeType, customConfig) => onAddNodeFromNodeRef.current?.(nodeId, nodeType, customConfig)
            }
          };
        }

        // Always update nodeTemplates for all nodes
        return {
          ...node,
          data: {
            ...node.data,
            onDelete: () => deleteNodeRef.current?.(node.id),
            onExecute: () => handleExecuteWorkflowRef.current?.(),
            nodeTemplates: nodeTemplates,
            onAddNodeFromNode: (nodeId, nodeType, customConfig) => onAddNodeFromNodeRef.current?.(nodeId, nodeType, customConfig)
          }
        };
      }));
    }
  }, [nodeToolsLoaded, nodeTemplates]); // Remove nodes from dependencies to avoid infinite loops

  // Additional effect to ensure nodes always have the latest nodeTemplates
  useEffect(() => {
    // Only run when nodeTemplates change and are loaded
    if (Object.keys(nodeTemplates).length > 0 && nodes.length > 0) {
      // Check if any node is missing nodeTemplates
      const nodesWithoutTemplates = nodes.filter(node => !node.data?.nodeTemplates);

      if (nodesWithoutTemplates.length > 0) {
        setNodes(prevNodes => prevNodes.map(node => {
          if (!node.data?.nodeTemplates) {
            return {
              ...node,
              data: {
                ...node.data,
                nodeTemplates: { ...nodeTemplates }
              }
            };
          }
          return node;
        }));
      }
    }
  }, [nodeTemplates, nodes.length]); // Depend on nodes.length instead of nodes to reduce re-renders

  // Load dynamic node tools from database
  useEffect(() => {
    const loadNodeTools = () => {
      try {
        if (!nodeTools.loading && !nodeToolsLoaded) {
          dispatch(getNodeTools());
        }

        // Process node tools when they become available
        if (nodeTools.data.length > 0 && !nodeToolsLoaded) {
          // Process nodeTools.data directly - it's already a flat array
          const nodesList = Array.isArray(nodeTools.data) ? nodeTools.data : [];

          // Create dynamic node types mapping - all nodes map to WorkflowNode
          const dynamicNodeTypes = {};
          const templates = {};

          nodesList.forEach(nodeTemplate => {
            const nodeType = nodeTemplate.nodeDefinition?.nodeType;
            if (nodeType) {
              // Map each node type to WorkflowNode component for ReactFlow
              dynamicNodeTypes[nodeType] = WorkflowNode;

              // Store node metadata for UI rendering
              templates[nodeType] = {
                label: nodeTemplate.name,
                icon: <div dangerouslySetInnerHTML={{ __html: nodeTemplate.iconSvg || '?' }} />,
                color: nodeTemplate.nodeDefinition?.style?.color || '#6B7280',
                description: nodeTemplate.description,
                category: nodeTemplate.nodeDefinition?.nodeCategory || 'other',
                template: nodeTemplate
              };
            }
          });

          // Add backward compatibility
          dynamicNodeTypes.dynamicNode = WorkflowNode;

          // Preserve the Proxy behavior when updating nodeTypes
          setNodeTypes(prevTypes => {
            // Create a new object that includes both existing and new types
            const allTypes = {
              ...dynamicNodeTypes,
              // Include ReactFlow default types
              input: WorkflowNode,
              default: WorkflowNode,
              output: WorkflowNode,
              group: WorkflowNode
            };

            // Return a new Proxy with all types
            return new Proxy(allTypes, {
              get: (target, prop) => {
                if (prop in target) return target[prop];
                console.warn(`[ReactFlowBuilder] Unknown node type requested: ${prop}, using WorkflowNode fallback`);
                return WorkflowNode;
              }
            });
          });
          setNodeTemplates(templates);
          setNodeToolsLoaded(true);
        }
      } catch (error) {
        console.error('[ReactFlowBuilder] Error loading node tools:', error);
      }
    };

    loadNodeTools();
  }, [dispatch, nodeTools, nodeToolsLoaded]);

  // Listen for collaborative changes
  useEffect(() => {
    if (!workflowId || !websocketService.isConnected()) return;

    const handleNodeLock = (data) => {
      if (data.workflowId === workflowId)
        setLockedNodes(prev => {
          const updated = new Set(prev);
          if (data.locked) updated.add(data.nodeId);
          else updated.delete(data.nodeId);
          return updated;
        })
    }

    const handleWorkflowChange = (data) => {
      if (data.workflowId === workflowId) {
        switch (data.changeType) {
          case 'node:add':
            setNodes(nds => [...nds, data.data.node]);
            break;
          case 'node:update':
            setNodes(nds => nds.map(n => n.id === data.data.nodeId ? { ...n, ...data.data.updates } : n));
            break;
          case 'node:delete':
            setNodes(nds => nds.filter(n => n.id !== data.data.nodeId));
            break;
          case 'edge:add':
            setEdges(eds => [...eds, data.data.edge]);
            break;
          case 'edge:delete':
            setEdges(eds => eds.filter(e => e.id !== data.data.edgeId));
            break;
        }
      }
    }

    const unsubscribeNodeLock = EventBus.on('workflow:node:lock', handleNodeLock);
    const unsubscribeWorkflowChange = EventBus.on('workflow:change', handleWorkflowChange);
    return () => {
      unsubscribeNodeLock();
      unsubscribeWorkflowChange();
    };
  }, [workflowId]);

  const handleClickArrowBottom = () => setIsActive((prev) => !prev);

  // Handle node changes
  const onNodesChange = useCallback(
    (changes) => {
      setNodes((nds) => applyNodeChanges(changes, nds));
      // Mark as dirty when nodes change (except selection changes)
      const hasActualChanges = changes.some(change =>
        change.type !== 'select' &&
        !(change.type === 'position' && change.dragging === true)
      );
      if (hasActualChanges && !readOnly) {
        setIsDirty(true);
      }

    }, [workflowId, readOnly]
  );

  // Handle edge changes
  const onEdgesChange = useCallback(
    (changes) => {
      // Mark as dirty when edges change (except selection changes)
      const hasActualChanges = changes.some(change => change.type !== 'select');
      if (hasActualChanges && !readOnly) {
        setIsDirty(true);
      }

      // Handle edge removals to update node connections
      const removedEdges = changes
        .filter(change => change.type === 'remove')
        .map(change => edges.find(e => e.id === change.id))
        .filter(Boolean);

      if (removedEdges.length > 0) {
        setNodes((nds) => {
          return nds.map(node => {
            const connections = { ...node.data.connections } || { inputs: {}, outputs: {} };
            let updated = false;
            removedEdges.forEach(edge => {
              // Remove input connections for this edge
              if (node.id === edge.target && node.type === 'llm' && edge.targetHandle) {
                if (connections.inputs && connections.inputs[edge.targetHandle]) {
                  delete connections.inputs[edge.targetHandle];
                  updated = true;
                }
              }
            });

            if (updated) return { ...node, data: { ...node.data, connections } }
            return node;
          });
        });
      }

      setEdges((eds) => {
        const updatedEdges = applyEdgeChanges(changes, eds);
        // Propagate changes to parent if any meaningful change occurred
        const hasSignificantChange = changes.some(change => change.type === 'add' || change.type === 'remove' || change.type === 'reset')
        if (hasSignificantChange && onSave) wrappedOnSave({ nodes, edges: updatedEdges })
        return updatedEdges;
      })
    }, [nodes, edges, onSave, readOnly]
  )

  // Handle edge click
  const onEdgeClick = useCallback((event, edge) => {
    event.stopPropagation();
    setSelectedEdge(edge);
  }, []);

  // Delete edge function
  const deleteEdge = useCallback((edgeId) => {
    // Find the edge to be deleted
    const edgeToDelete = edges.find(e => e.id === edgeId);
    if (!edgeToDelete) return;

    // Update nodes to remove connections
    setNodes((nds) => {
      return nds.map(node => {
        let updated = false;
        const connections = { ...node.data.connections } || { inputs: {}, outputs: {} };

        // Remove input connections for this edge
        if (node.id === edgeToDelete.target && node.type === 'llm' && edgeToDelete.targetHandle) {
          if (connections.inputs && connections.inputs[edgeToDelete.targetHandle]) {
            delete connections.inputs[edgeToDelete.targetHandle];
            updated = true;
          }
        }
        if (updated) return { ...node, data: { ...node.data, connections } }
        return node;
      });
    });

    // Remove the edge
    setEdges((eds) => eds.filter((e) => e.id !== edgeId));
    setSelectedEdge(null);

    // Emit change for collaboration

    // Update parent
    if (onSave) onSave({ nodes, edges: edges.filter(e => e.id !== edgeId) });
  }, [edges, nodes, onSave]);

  // Create a ref to store deleteEdge function
  const deleteEdgeRef = useRef(deleteEdge);
  deleteEdgeRef.current = deleteEdge;

  // Handle connection start to capture sourceHandle
  const onConnectStart = useCallback((event, params) => {
    if (params && params.handleId) {
      setConnectionStartHandle(params.handleId);
    }
  }, []);

  // Handle connection end to reset state
  const onConnectEnd = useCallback(() => {
    setConnectionStartHandle(null);
  }, []);

  // Handle new connections
  const onConnect = useCallback(
    (params) => {
      // Edge creation with proper string conversion for sourceHandle
      let sourceHandle = params.sourceHandle || connectionStartHandle;
      
      // Ensure sourceHandle is always a string, never boolean or null
      if (sourceHandle === true) {
        sourceHandle = 'true';
      } else if (sourceHandle === false) {
        sourceHandle = 'false';
      } else if (sourceHandle === null || sourceHandle === undefined) {
        sourceHandle = undefined; // Keep as undefined if truly not set
      } else if (typeof sourceHandle !== 'string' && sourceHandle !== undefined) {
        sourceHandle = String(sourceHandle); // Convert other types to string
      }
      
      // Find source node to get its type for decision node detection
      const sourceNode = nodes.find(node => node.id === params.source);
      const sourceNodeType = sourceNode?.type;
      
      // Determine edge type based on source node
      let edgeType = 'smoothstep';
      if (sourceNodeType === 'decision') {
        edgeType = 'conditional';
      }

      const newEdge = {
        ...params,
        animated: true,
        type: edgeType,
        id: `edge_${edges.length + 1}`,
        sourceHandle: sourceHandle,
        targetHandle: params.targetHandle,
        style: { strokeWidth: 2, stroke: '#b1b1b7' },
        data: { 
          sourceNodeType: sourceNodeType, // Add source node type for decision detection
          onDelete: (edgeId) => deleteEdgeRef.current(edgeId),
          nodeTemplates: nodeTemplates,
          onAddNode: (edgeId, nodeType, customConfig) => {
            if (onAddNodeFromEdgeRef.current) {
              onAddNodeFromEdgeRef.current(edgeId, nodeType, customConfig);
            } else {
              EventBus.publish('error', 'Unable to add node. Please try again.');
            }
          }
        },
        markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20, color: '#b1b1b7' },
      };

      // Update node connections for LLM nodes
      setNodes((nds) => {
        return nds.map(node => {
          // Update target node connections
          if (node.id === params.target) {
            const connections = node.data.connections || { inputs: {}, outputs: {} };

            // For LLM nodes, track input connections by handle ID
            if (node.type === 'llm' && params.targetHandle) {
              connections.inputs[params.targetHandle] = {
                sourceNode: params.source,
                sourceOutput: params.sourceHandle || 'output'
              };
            }

            return { ...node, data: { ...node.data, connections } }
          }
          return node
        })
      })

      setEdges((eds) => {
        const updatedEdges = addEdge(newEdge, eds);        
        // Propagate changes to parent
        if (onSave) wrappedOnSave({ nodes, edges: updatedEdges });
        return updatedEdges;
      })

    }, [edges, nodes, onSave, nodeTemplates]
  )

  // Comprehensive edge update effect - ensures all edges have nodeTemplates and callbacks
  useEffect(() => {
    // Ensure we have nodeTemplates loaded before enriching edges
    if (Object.keys(nodeTemplates).length > 0 && edges.length > 0) {
      // Add a small delay to ensure React Flow has rendered the edges
      const timeoutId = setTimeout(() => {
        // Only update edges that actually need enrichment
        setEdges(currentEdges => {
          return currentEdges.map(edge => {
            // Find source node to get its type for decision node detection
            const sourceNode = nodes.find(node => node.id === edge.source);
            const sourceNodeType = sourceNode?.type;

            // Assign sourceHandle for decision nodes if missing (same logic as WorkflowBuilder)
            let finalSourceHandle = edge.sourceHandle;
            if (sourceNodeType === 'decision' && (edge.sourceHandle === undefined || edge.sourceHandle === null)) {
              // Find all edges from this decision node
              const allEdgesFromDecision = currentEdges.filter(e => e.source === edge.source);
              
              // Sort edges consistently (by creation order based on edge ID, then by target)
              const sortedEdges = [...allEdgesFromDecision].sort((a, b) => {
                const aNum = parseInt(a.id.replace(/\D/g, '')) || 0;
                const bNum = parseInt(b.id.replace(/\D/g, '')) || 0;
                
                if (aNum !== bNum) {
                  return aNum - bNum; // Earlier created edge gets 'true'
                }
                
                return a.target.localeCompare(b.target);
              });
              
              const edgeIndex = sortedEdges.findIndex(e => e.id === edge.id);
              finalSourceHandle = edgeIndex === 0 ? 'true' : 'false';
            }

            // Create a new edge object to force React to re-render
            // IMPORTANT: Explicitly preserve and assign sourceHandle
            const updatedEdge = {
              ...edge,
              id: edge.id,                      // Explicitly preserve edge ID
              source: edge.source,              // Explicitly preserve source
              target: edge.target,              // Explicitly preserve target
              sourceHandle: finalSourceHandle,  // Use assigned or preserved sourceHandle
              targetHandle: edge.targetHandle,  // Explicitly preserve targetHandle
              type: edge.type || 'smoothstep',  // Preserve existing type with fallback
              data: {
                ...edge.data,
                sourceNodeType: sourceNodeType, // Add source node type for decision detection
                onDelete: (edgeId) => deleteEdgeRef.current(edgeId),
                nodeTemplates: { ...nodeTemplates }, // Create new object reference
                onAddNode: (edgeId, nodeType, customConfig) => {
                  if (onAddNodeFromEdgeRef.current) {
                    onAddNodeFromEdgeRef.current(edgeId, nodeType, customConfig);
                  } else {
                    console.error('[ReactFlowBuilder] onAddNodeFromEdgeRef not available');
                  }
                }
              }
            };
            
            return updatedEdge;
          });
        });
      }, 100); // Small delay to ensure rendering

      return () => clearTimeout(timeoutId);
    }
  }, [nodeTemplates, edges.length, JSON.stringify(edges.map(e => ({id: e.id, source: e.source, hasData: !!e.data?.sourceNodeType})))]); // Smart dependency: only re-run when structure changes, not on every edge update

  // Additional effect to handle edges added after nodeTemplates are loaded
  useEffect(() => {
    if (edges.length > 0 && Object.keys(nodeTemplates).length > 0) {
      // Check if the last edge has nodeTemplates
      const lastEdge = edges[edges.length - 1];
      if (!lastEdge.data?.nodeTemplates) {
        setEdges(currentEdges => currentEdges.map((edge, index) => {
          if (index === currentEdges.length - 1 && !edge.data?.nodeTemplates) {
            return {
              ...edge,
              data: {
                ...edge.data,
                onDelete: (edgeId) => deleteEdgeRef.current(edgeId),
                nodeTemplates: { ...nodeTemplates },
                onAddNode: (edgeId, nodeType, customConfig) => {
                  if (onAddNodeFromEdgeRef.current) {
                    onAddNodeFromEdgeRef.current(edgeId, nodeType, customConfig);
                  }
                }
              }
            };
          }
          return edge;
        }));
      }
    }
  }, [edges.length, nodeTemplates]);

  // Add new node
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    async (event) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const type = event.dataTransfer.getData('application/reactflow');
      if (typeof type === 'undefined' || !type) return;

      const position = project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      // Check if it's a dynamic node type first
      const nodeTemplate = nodeTemplates[type];
      let newNode;
      if (nodeTemplate) {
        // Create dynamic node instance using local function
        try {
          // Check if custom config was passed in the dataTransfer
          const customConfigStr = event.dataTransfer.getData('customConfig');
          const customConfig = customConfigStr ? JSON.parse(customConfigStr) : {};

          newNode = createNodeInstance(nodeTemplate.template, position, customConfig);
          newNode.id = `node_${nodeIdCounter}`; // Override with sequential ID

          // Add onDelete and onExecute callbacks to node data
          newNode.data = {
            ...newNode.data,
            onDelete: () => deleteNodeRef.current?.(newNode.id),
            onExecute: () => handleExecuteWorkflowRef.current?.(),
            nodeTemplates: nodeTemplates,
            onAddNodeFromNode: (nodeId, nodeType, customConfig) => onAddNodeFromNodeRef.current?.(nodeId, nodeType, customConfig)
          };
        } catch (error) {
          console.error('[ReactFlowBuilder] Error creating dynamic node:', error);
          EventBus.publish('error', 'Failed to create node');
          return;
        }
      } else {
        console.warn('[ReactFlowBuilder] Unknown node type:', type);
        return;
      }

      setNodes((nds) => {
        const updatedNodes = nds.concat(newNode);
        // Call onSave after adding the node
        if (onSave) setTimeout(() => { wrappedOnSave({ nodes: updatedNodes, edges }) }, 0);
        return updatedNodes;
      });
      setNodeIdCounter(nodeIdCounter + 1);

    }, [project, nodeIdCounter, edges, onSave, nodeTemplates]
  )

  // Handler for adding node from edge popup
  const onAddNodeFromEdge = useCallback((edgeId, nodeType, customConfig = {}) => {
    const edge = edges.find(e => e.id === edgeId);
    if (!edge) {
      console.error('[ReactFlowBuilder] Edge not found:', edgeId);
      EventBus.publish('error', 'Edge not found');
      return;
    }

    const sourceNode = nodes.find(n => n.id === edge.source);
    const targetNode = nodes.find(n => n.id === edge.target);

    if (!sourceNode || !targetNode) {
      console.error('[ReactFlowBuilder] Source or target node not found');
      return;
    }

    // Position new node at midpoint
    const midX = (sourceNode.position.x + targetNode.position.x) / 2;
    const midY = (sourceNode.position.y + targetNode.position.y) / 2;

    // Push existing nodes apart to create space (150px each direction)
    const spacing = 150;
    const dx = targetNode.position.x - sourceNode.position.x;
    const dy = targetNode.position.y - sourceNode.position.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    const offsetX = (dx / len) * spacing;
    const offsetY = (dy / len) * spacing;

    setNodes(nds => nds.map(n =>
      n.id === sourceNode.id ? { ...n, position: { x: n.position.x - offsetX, y: n.position.y - offsetY } } :
      n.id === targetNode.id ? { ...n, position: { x: n.position.x + offsetX, y: n.position.y + offsetY } } : n
    ));

    const position = { x: midX, y: midY };

    const nodeTemplate = nodeTemplates[nodeType];
    if (!nodeTemplate) {
      console.error('[ReactFlowBuilder] Node template not found:', { nodeType, availableTypes: Object.keys(nodeTemplates) });
      EventBus.publish('error', `Node type "${nodeType}" not found`);
      return;
    }

    try {
      const newNode = createNodeInstance(nodeTemplate.template, position, customConfig);
      newNode.id = `node_${nodeIdCounter}`;
      newNode.data = {
        ...newNode.data,
        onDelete: () => deleteNodeRef.current?.(newNode.id),
        onExecute: () => handleExecuteWorkflowRef.current?.(),
        nodeTemplates: nodeTemplates,
        onAddNodeFromNode: (nodeId, nodeType, customConfig) => onAddNodeFromNodeRef.current?.(nodeId, nodeType, customConfig)
      };

      const newEdge1 = { ...edge, id: `edge_${edges.length + 1}`, target: newNode.id, targetHandle: null };
      const newEdge2 = { ...edge, id: `edge_${edges.length + 2}`, source: newNode.id, sourceHandle: null, target: edge.target };

      // Update nodes and edges in single batch
      setNodes(nds => [...nds, newNode]);
      setEdges(eds => {
        const updatedEdges = [...eds.filter(e => e.id !== edgeId), newEdge1, newEdge2];
        if (onSave) setTimeout(() => onSave({ nodes: [...nodes, newNode], edges: updatedEdges }), 0);
        return updatedEdges;
      });

      setNodeIdCounter(nodeIdCounter + 1);
    } catch (error) {
      console.error('[ReactFlowBuilder] Error adding node from edge:', error);
      EventBus.publish('error', 'Failed to add node');
    }
  }, [nodes, edges, nodeTemplates, nodeIdCounter, onSave]);

  // Update ref for onAddNodeFromEdge immediately
  useEffect(() => {
    onAddNodeFromEdgeRef.current = onAddNodeFromEdge;
  }, [onAddNodeFromEdge]);

  // Handler for adding node from node popup
  const onAddNodeFromNode = useCallback((sourceNodeId, nodeType, customConfig = {}) => {

    const sourceNode = nodes.find(n => n.id === sourceNodeId);
    if (!sourceNode) {
      console.error('[ReactFlowBuilder] Source node not found:', sourceNodeId);
      EventBus.publish('error', 'Source node not found');
      return;
    }

    // Calculate position for new node (offset to the right and slightly down)
    const position = {
      x: sourceNode.position.x + 250,
      y: sourceNode.position.y + 50
    };

    // Create new node
    const nodeTemplate = nodeTemplates[nodeType];
    if (!nodeTemplate) {
      console.error('[ReactFlowBuilder] Node template not found:', { nodeType, availableTypes: Object.keys(nodeTemplates) });
      EventBus.publish('error', `Node type "${nodeType}" not found`);
      return;
    }

    try {
      const newNode = createNodeInstance(nodeTemplate.template, position, customConfig);
      newNode.id = `node_${nodeIdCounter}`;

      // Add callbacks and nodeTemplates
      newNode.data = {
        ...newNode.data,
        onDelete: () => deleteNodeRef.current?.(newNode.id),
        onExecute: () => handleExecuteWorkflowRef.current?.(),
        nodeTemplates: nodeTemplates,
        onAddNodeFromNode: (nodeId, type, config) => onAddNodeFromNodeRef.current?.(nodeId, type, config)
      };

      // Update both nodes and edges together to ensure consistency
      setNodes(nds => {
        const updatedNodes = [...nds, newNode];

        // Update edges with the new edge
        setEdges(eds => {
          // Determine sourceHandle for decision nodes INSIDE setEdges to get current state
          let sourceHandle = undefined;
          let edgeType = 'smoothstep';

          if (sourceNode.type === 'decision') {
            // Count existing edges from this decision node using CURRENT edges state
            const existingDecisionEdges = eds.filter(e => e.source === sourceNodeId);

            // First edge = 'true', second edge = 'false', alternating after
            if (existingDecisionEdges.length === 0) {
              sourceHandle = 'true';
            } else if (existingDecisionEdges.length === 1) {
              sourceHandle = 'false';
            } else {
              // For 3+ edges, check what's available
              const hasTrueEdge = existingDecisionEdges.some(e => e.sourceHandle === 'true');
              sourceHandle = hasTrueEdge ? 'false' : 'true';
            }

            edgeType = 'conditional'; // Decision node edges should be conditional type
          }

          // Create new edge from source to new node
          const newEdge = {
            id: `edge_${eds.length + 1}_${Date.now()}`, // Use current edges length + timestamp for uniqueness
            source: sourceNodeId,
            target: newNode.id,
            sourceHandle: sourceHandle,
            type: edgeType,
            animated: true,
            style: { strokeWidth: 2, stroke: '#b1b1b7' },
            data: {
              sourceNodeType: sourceNode.type, // Add sourceNodeType for CustomEdge detection
              onDelete: (edgeId) => deleteEdgeRef.current(edgeId),
              nodeTemplates: nodeTemplates,
              onAddNode: (edgeId, nodeType, customConfig) => {
                if (onAddNodeFromEdgeRef.current) {
                  onAddNodeFromEdgeRef.current(edgeId, nodeType, customConfig);
                }
              }
            },
            markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20, color: '#b1b1b7' }
          };

          const updatedEdges = [...eds, newEdge];

          // Save with both updated states
          if (onSave) {
            setTimeout(() => wrappedOnSave({ nodes: updatedNodes, edges: updatedEdges }), 0);
          }

          return updatedEdges;
        });

        return updatedNodes;
      });

      setNodeIdCounter(nodeIdCounter + 1);

    } catch (error) {
      console.error('[ReactFlowBuilder] Error adding node from node:', error);
      EventBus.publish('error', 'Failed to add node');
    }
  }, [nodes, edges, nodeTemplates, nodeIdCounter, onSave]);

  // Update ref for onAddNodeFromNode immediately
  useEffect(() => {
    onAddNodeFromNodeRef.current = onAddNodeFromNode;
  }, [onAddNodeFromNode]);

  // Update nodes with hasOutgoingConnections property
  useEffect(() => {
    if (nodes.length > 0 && edges.length >= 0) {
      setNodes(currentNodes => {
        return currentNodes.map(node => {
          const hasOutgoingConnections = edges.some(edge => edge.source === node.id);

          // Only update if the value has changed
          if (node.data?.hasOutgoingConnections !== hasOutgoingConnections) {
            return {
              ...node,
              data: {
                ...node.data,
                hasOutgoingConnections
              }
            };
          }
          return node;
        });
      });
    }
  }, [edges]); // Only depend on edges, not nodes to avoid infinite loops

  // Node click handler
  const onNodeClick = useCallback((event, node) => {

    // Check if click is from popup area - if so, ignore
    if (event.target.closest('.node-popup-edit')) {
      return;
    }

    // Check if node is locked by another user
    if (lockedNodes.has(node.id)) {
      EventBus.publish('warning', 'This node is being edited by another user');
      return;
    }

    setSelectedNode(node);
    if (!readOnly) {
      setConfigDialogOpen(true);
      // Lock the node for editing
    }
  }, [readOnly, lockedNodes]);

  // Navigate to previous node
  const handlePreviousNode = useCallback(() => {
    if (!selectedNode || nodes.length === 0) return;

    // Sort nodes by position (left to right based on x coordinate)
    const sortedNodes = [...nodes].sort((a, b) => a.position.x - b.position.x);
    const currentIndex = sortedNodes.findIndex(n => n.id === selectedNode.id);

    if (currentIndex > 0) {
      const previousNode = sortedNodes[currentIndex - 1];

      // Unlock current node
      if (workflowId) emitNodeLock(selectedNode.id, false);

      // Set and lock new node
      setSelectedNode(previousNode);
      if (workflowId) emitNodeLock(previousNode.id, true);

    }
  }, [selectedNode, nodes, workflowId, emitNodeLock]);

  // Navigate to next node
  const handleNextNode = useCallback(() => {
    if (!selectedNode || nodes.length === 0) return;

    // Sort nodes by position (left to right based on x coordinate)
    const sortedNodes = [...nodes].sort((a, b) => a.position.x - b.position.x);
    const currentIndex = sortedNodes.findIndex(n => n.id === selectedNode.id);

    if (currentIndex < sortedNodes.length - 1) {
      const nextNode = sortedNodes[currentIndex + 1];

      // Unlock current node
      if (workflowId) emitNodeLock(selectedNode.id, false);

      // Set and lock new node
      setSelectedNode(nextNode);
      if (workflowId) emitNodeLock(nextNode.id, true);

    }
  }, [selectedNode, nodes, workflowId, emitNodeLock]);

  // Delete selected elements
  const deleteSelectedElements = useCallback(() => {
    const selectedNodeIds = nodes.filter(n => n.selected).map(n => n.id);
    const selectedEdgeIds = edges.filter(e => e.selected).map(e => e.id);

    let updatedNodes = [], updatedEdges = [];

    // Remove selected nodes
    setNodes((nds) => {
      updatedNodes = nds.filter((node) => !node.selected);
      return updatedNodes;
    });

    // Remove selected edges AND edges connected to deleted nodes
    setEdges((eds) => {
      updatedEdges = eds.filter((edge) => {
        // Remove if edge is selected
        if (edge.selected) return false;

        // Remove if edge is connected to a deleted node
        if (selectedNodeIds.includes(edge.source) || selectedNodeIds.includes(edge.target))
          return false;

        return true;
      });
      return updatedEdges;
    });

    // Call onSave after deletion
    if (onSave) setTimeout(() => { onSave({ nodes: updatedNodes, edges: updatedEdges }) }, 0);
  }, [nodes, edges, onSave]);


  // Save workflow
  const handleSave = async () => {
    if (onSave) {

      // Update WorkflowNodeService with current templates
      workflowNodeService.updateNodeTemplates(nodeTemplates);

      // Clean up any orphaned edges before saving
      const nodeIds = new Set(nodes.map(n => n.id));
      const cleanedEdges = edges.filter(edge => nodeIds.has(edge.source) && nodeIds.has(edge.target));

      if (cleanedEdges.length !== edges.length) {
        console.warn('Removed orphaned edges:', edges.length - cleanedEdges.length);
        setEdges(cleanedEdges);
      }

      // Process all nodes using WorkflowNodeService
      const processedNodes = await Promise.all(nodes.map(async (node) => {
        try {
          return await workflowNodeService.processNodeForSave(node);
        } catch (error) {
          console.error(`Error processing node ${node.id}:`, error);
          toast.error(`Validation error in node ${node.data?.label || node.id}`);
          return null;
        }
      }));

      // Filter out any nodes that failed processing
      const validNodes = processedNodes.filter(node => node !== null);

      if (validNodes.length !== nodes.length) {
        toast.error('Some nodes have validation errors. Please fix them before saving.');
        return;
      }

      onSave({ nodes: validNodes, edges: cleanedEdges });
    }
  };

  // Update node configuration
  const handleNodeConfigSave = async (nodeId, newData) => {

    // Mark as dirty when node configuration changes
    if (!readOnly) {
      setIsDirty(true);
    }

    // Update WorkflowNodeService with current templates
    workflowNodeService.updateNodeTemplates(nodeTemplates);

    setNodes((nds) => {
      const updatedNodes = nds.map((node) => {
        if (node.id === nodeId) {
          // Ensure nodeDefinition is preserved
          const updatedNodeData = {
            ...newData,
            // Always preserve nodeDefinition if it exists in current node
            nodeDefinition: newData.nodeDefinition || node.data?.nodeDefinition,
            iconSvg: newData.iconSvg || node.data?.iconSvg
          };

          const updatedNode = { ...node, data: updatedNodeData };

          // Use WorkflowNodeService to check behaviors instead of hardcoded logic
          if (workflowNodeService.requiresIntegration(updatedNode.type)) {
            const requirements = workflowNodeService.getIntegrationRequirements(updatedNode.type, updatedNode.data?.config || {});
          }

          return updatedNode;
        }
        return node;
      });

      // Immediately propagate changes to parent component
      if (onSave) {
        // Don't process nodes here - just save the updated nodes
        // Processing will happen in handleSave when the user explicitly saves
        wrappedOnSave({ nodes: updatedNodes, edges });
      }

      return updatedNodes;
    })

    // Unlock the node
    if (workflowId) emitNodeLock(nodeId, false);
  };

  // Drag start handler for node palette
  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  const [isDragging, setIsDragging] = useState(false);
  const ArrowBottom = (action) => (e) => {
    if (isDragging) {
      e.stopPropagation();
      setDrawerOpen(false);
      return; // prevent clicks after drag
    }
    action();
  };

  const addNode = async (type, customConfig = {}) => {
    // All nodes are now dynamic - loaded from database
    const nodeTemplate = nodeTemplates[type];

    if (!nodeTemplate) {
      return;
    }

    // Create dynamic node using local function with custom config
    try {
      const newNode = createNodeInstance(nodeTemplate.template, { x: 10, y: 10 }, customConfig);
      newNode.id = `${type}-${+new Date()}`; // Override with click-based ID

      // Add onDelete and onExecute callbacks to node data
      newNode.data = {
        ...newNode.data,
        onDelete: () => deleteNode(newNode.id),
        onExecute: () => handleExecuteWorkflow(),
        nodeTemplates: nodeTemplates,
        onAddNodeFromNode: (nodeId, nodeType, customConfig) => onAddNodeFromNodeRef.current?.(nodeId, nodeType, customConfig)
      };

      setNodes((nds) => [...nds, newNode]);

      // If config was provided, it means a preset was selected, so open the config dialog
      if (Object.keys(customConfig).length > 0) {
        setSelectedNode(newNode);
        setConfigDialogOpen(true);
      }
    } catch (error) {
      console.error('[ReactFlowBuilder] Error creating dynamic node in addNode:', error);
    }
  }

  // Only show loading state if we have no nodes to display AND nodeTools are loading
  if (!nodeToolsLoaded && nodeTools.loading && initialNodes.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading workflow nodes...</Typography>
      </Box>
    )
  }

  // Show error state if nodeTools failed to load AND we have no nodes
  if (!nodeToolsLoaded && nodeTools.error && initialNodes.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Alert severity="error">
          <Typography>Failed to load workflow nodes: {nodeTools.error}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <ReactFlowProvider>
      <Box sx={{ display: 'flex', height: '100%', position: 'relative' }}>
        {/* Node Palette Drawer */}
        <Drawer
          variant="persistent"
          anchor="left"
          open={drawerOpen}
          className='leftside-node-bar'
          sx={{
            width: drawerOpen ? 300 : 0,
            flexShrink: 0,
            '& .MuiDrawer-paper': { width: 300, boxSizing: 'border-box', position: 'relative', height: '100%' },
          }}
        >
          <div className='left-side-bar'>
            <div className='title-area'>
              <div className='title-top'>
                <h4>Node Library</h4>
                <p>Drag nodes to the canvas to add them to your workflow</p>
                <IconButton onClick={() => setDrawerOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </div>
            </div>
            <NodeLibrary
              nodeTemplates={nodeTemplates}
              onDragStart={onDragStart}
              readOnly={readOnly}
              searchable={true}
              compact={false}
              activeType={activeType}
              onToggleType={handleToggle}
            />

            {/* Loading state */}
            {!nodeToolsLoaded && Object.keys(nodeTemplates).length === 0 && (
              <div className='list-item' style={{ justifyContent: 'center', opacity: 0.7, padding: '20px' }}>
                {nodeTools.loading ? (
                  <p>Loading nodes...</p>
                ) : nodeTools.error ? (
                  <div>
                    <p style={{ color: '#ef4444', marginBottom: '4px' }}>Error loading nodes</p>
                    <p style={{ fontSize: '12px', color: '#6b7280' }}>{nodeTools.error}</p>
                  </div>
                ) : (
                  <p>No nodes available</p>
                )}
              </div>
            )}
          </div>
        </Drawer>

        {/* ReactFlow Canvas */}
        <Box sx={{ flex: 1, position: 'relative' }} ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onConnectStart={onConnectStart}
            onConnectEnd={onConnectEnd}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onDrop={onDrop}
            onDragOver={onDragOver}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            defaultEdgeOptions={defaultEdgeOptions}
            edgesSelectable={true}
            fitView
            attributionPosition="top-right"
            deleteKeyCode={readOnly ? null : 'Delete'}
            onKeyDown={(e) => {
              if (e.key === 'Delete' && !readOnly) deleteSelectedElements();
            }}
          >
            {/* <Background variant={BackgroundVariant.Dots} gap={12} size={1}  /> */}
            <Background
              id="light-dots"
              color="#ccc"   // light gray
              gap={20}         // distance between dots
              variant={BackgroundVariant.Dots}
              size={1}       // dot size
            />

            {/* Custom Control Panel */}
            <Panel position="bottom-left" >
              <div
                className={
                  `control-panel-wrapper` +
                  (isActive ? ' active' : '') +
                  (advanceSetting ? ' advance-options' : '')
                }
              >
                <Tooltip title="Show Node Library">
                  <button className='play-btn' size="small" onClick={() => setDrawerOpen(true)}>
                    <i className='icon'>
                      <svg width="800px" height="800px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 12H18M12 6V18" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>
                    </i>
                    Add Node
                  </button>
                </Tooltip>
                <Tooltip title="Run Once">
                  <button size="small" onClick={handleExecuteWorkflow} disabled={readOnly || !workflowId}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4.59081 12.4134C4.12998 12.4134 3.69248 12.3025 3.30748 12.0809C2.39748 11.5559 1.89581 10.4884 1.89581 9.08253V4.92337C1.89581 3.5117 2.39748 2.45003 3.30748 1.92503C4.21748 1.40003 5.38998 1.4992 6.61498 2.20503L10.2141 4.2817C11.4333 4.98753 12.11 5.95587 12.11 7.00587C12.11 8.05587 11.4391 9.0242 10.2141 9.73003L6.61498 11.8067C5.90915 12.2092 5.22081 12.4134 4.59081 12.4134ZM4.59081 2.4617C4.27581 2.4617 3.99581 2.5317 3.74498 2.67753C3.11498 3.0392 2.77081 3.83837 2.77081 4.92337V9.0767C2.77081 10.1617 3.11498 10.955 3.74498 11.3225C4.37498 11.69 5.23831 11.585 6.17748 11.0425L9.77665 8.96587C10.7158 8.42337 11.235 7.7292 11.235 7.00003C11.235 6.27087 10.7158 5.5767 9.77665 5.0342L6.17748 2.95753C5.60581 2.63087 5.06915 2.4617 4.59081 2.4617Z" fill="#4B5563" />
                    </svg>
                  </button>
                </Tooltip>
                {saving ? (
                  <Tooltip title="Saving...">
                    <button size="small" disabled style={{ cursor: 'wait' }}>
                      <CircularProgress size={14} style={{ color: '#4B5563' }} />
                    </button>
                  </Tooltip>
                ) : !isDirty && lastSavedAt ? (
                  <Tooltip title={`Saved at ${lastSavedAt.toLocaleTimeString()}`}>
                    <button size="small" disabled style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span style={{ fontSize: '12px', fontWeight: '500' }}>Saved</span>
                    </button>
                  </Tooltip>
                ) : (
                  <Tooltip title={isDirty ? "Save Changes" : "Save Workflow"}>
                    <button
                      size="small"
                      onClick={async () => {
                        // First save the nodes and edges locally
                        await handleSave();
                        // Then trigger the full workflow save
                        if (onSaveWorkflow) {
                          onSaveWorkflow();
                        }
                      }}
                      style={isDirty ? { backgroundColor: '#fbbf24', borderColor: '#f59e0b' } : {}}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H14L19 8V19C19 20.1046 18.1046 21 17 21Z" stroke={isDirty ? "#f59e0b" : "#4B5563"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M17 21V13H7V21" stroke={isDirty ? "#f59e0b" : "#4B5563"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M7 3V8H13" stroke={isDirty ? "#f59e0b" : "#4B5563"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {isDirty && <span style={{ width: '6px', height: '6px', backgroundColor: '#f59e0b', borderRadius: '50%', position: 'absolute', top: '2px', right: '2px' }}></span>}
                    </button>
                  </Tooltip>
                )}
                <Tooltip title="Advanced Settings">
                  <button size="small" onClick={() => setAdvanceSetting(prev => !prev)}>
                    <svg fill="#4B5563" width="14" height="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M16 12a4 4 0 11-8 0 4 4 0 018 0zm-1.5 0a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" /><path fill-rule="evenodd" d="M12 1c-.268 0-.534.01-.797.028-.763.055-1.345.617-1.512 1.304l-.352 1.45c-.02.078-.09.172-.225.22a8.45 8.45 0 00-.728.303c-.13.06-.246.044-.315.002l-1.274-.776c-.604-.368-1.412-.354-1.99.147-.403.348-.78.726-1.129 1.128-.5.579-.515 1.387-.147 1.99l.776 1.275c.042.069.059.185-.002.315-.112.237-.213.48-.302.728-.05.135-.143.206-.221.225l-1.45.352c-.687.167-1.249.749-1.304 1.512a11.149 11.149 0 000 1.594c.055.763.617 1.345 1.304 1.512l1.45.352c.078.02.172.09.22.225.09.248.191.491.303.729.06.129.044.245.002.314l-.776 1.274c-.368.604-.354 1.412.147 1.99.348.403.726.78 1.128 1.129.579.5 1.387.515 1.99.147l1.275-.776c.069-.042.185-.059.315.002.237.112.48.213.728.302.135.05.206.143.225.221l.352 1.45c.167.687.749 1.249 1.512 1.303a11.125 11.125 0 001.594 0c.763-.054 1.345-.616 1.512-1.303l.352-1.45c.02-.078.09-.172.225-.22.248-.09.491-.191.729-.303.129-.06.245-.044.314-.002l1.274.776c.604.368 1.412.354 1.99-.147.403-.348.78-.726 1.129-1.128.5-.579.515-1.387.147-1.99l-.776-1.275c-.042-.069-.059-.185.002-.315.112-.237.213-.48.302-.728.05-.135.143-.206.221-.225l1.45-.352c.687-.167 1.249-.749 1.303-1.512a11.125 11.125 0 000-1.594c-.054-.763-.616-1.345-1.303-1.512l-1.45-.352c-.078-.02-.172-.09-.22-.225a8.469 8.469 0 00-.303-.728c-.06-.13-.044-.246-.002-.315l.776-1.274c.368-.604.354-1.412-.147-1.99-.348-.403-.726-.78-1.128-1.129-.579-.5-1.387-.515-1.99-.147l-1.275.776c-.069.042-.185.059-.315-.002a8.465 8.465 0 00-.728-.302c-.135-.05-.206-.143-.225-.221l-.352-1.45c-.167-.687-.749-1.249-1.512-1.304A11.149 11.149 0 0012 1zm-.69 1.525a9.648 9.648 0 011.38 0c.055.004.135.05.162.16l.351 1.45c.153.628.626 1.08 1.173 1.278.205.074.405.157.6.249a1.832 1.832 0 001.733-.074l1.275-.776c.097-.06.186-.036.228 0 .348.302.674.628.976.976.036.042.06.13 0 .228l-.776 1.274a1.832 1.832 0 00-.074 1.734c.092.195.175.395.248.6.198.547.652 1.02 1.278 1.172l1.45.353c.111.026.157.106.161.161a9.653 9.653 0 010 1.38c-.004.055-.05.135-.16.162l-1.45.351a1.833 1.833 0 00-1.278 1.173 6.926 6.926 0 01-.25.6 1.832 1.832 0 00.075 1.733l.776 1.275c.06.097.036.186 0 .228a9.555 9.555 0 01-.976.976c-.042.036-.13.06-.228 0l-1.275-.776a1.832 1.832 0 00-1.733-.074 6.926 6.926 0 01-.6.248 1.833 1.833 0 00-1.172 1.278l-.353 1.45c-.026.111-.106.157-.161.161a9.653 9.653 0 01-1.38 0c-.055-.004-.135-.05-.162-.16l-.351-1.45a1.833 1.833 0 00-1.173-1.278 6.928 6.928 0 01-.6-.25 1.832 1.832 0 00-1.734.075l-1.274.776c-.097.06-.186.036-.228 0a9.56 9.56 0 01-.976-.976c-.036-.042-.06-.13 0-.228l.776-1.275a1.832 1.832 0 00.074-1.733 6.948 6.948 0 01-.249-.6 1.833 1.833 0 00-1.277-1.172l-1.45-.353c-.111-.026-.157-.106-.161-.161a9.648 9.648 0 010-1.38c.004-.055.05-.135.16-.162l1.45-.351a1.833 1.833 0 001.278-1.173 6.95 6.95 0 01.249-.6 1.832 1.832 0 00-.074-1.734l-.776-1.274c-.06-.097-.036-.186 0-.228.302-.348.628-.674.976-.976.042-.036.13-.06.228 0l1.274.776a1.832 1.832 0 001.734.074 6.95 6.95 0 01.6-.249 1.833 1.833 0 001.172-1.277l.353-1.45c.026-.111.106-.157.161-.161z" /></svg>
                  </button>
                </Tooltip>
                <Tooltip title="Delete Selected">
                  <button onClick={deleteSelectedElements} disabled={readOnly} size="small">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12.25 3.92578C12.2383 3.92578 12.2208 3.92578 12.2033 3.92578C9.11751 3.61661 6.03751 3.49995 2.98668 3.80911L1.79668 3.92578C1.55168 3.94911 1.33585 3.77411 1.31251 3.52911C1.28918 3.28411 1.46418 3.07411 1.70335 3.05078L2.89335 2.93411C5.99668 2.61911 9.14084 2.74161 12.2908 3.05078C12.53 3.07411 12.705 3.28995 12.6817 3.52911C12.6642 3.75661 12.4717 3.92578 12.25 3.92578Z" fill="#4B5563" />
                      <path d="M4.95832 3.33675C4.93499 3.33675 4.91165 3.33675 4.88249 3.33091C4.64915 3.29008 4.48582 3.06258 4.52665 2.82925L4.65499 2.06508C4.74832 1.50508 4.87665 0.729248 6.23582 0.729248H7.76415C9.12915 0.729248 9.25749 1.53425 9.34499 2.07091L9.47332 2.82925C9.51415 3.06841 9.35082 3.29591 9.11749 3.33091C8.87832 3.37175 8.65082 3.20841 8.61582 2.97508L8.48749 2.21675C8.40582 1.70925 8.38832 1.61008 7.76998 1.61008H6.24165C5.62332 1.61008 5.61165 1.69175 5.52415 2.21091L5.38999 2.96925C5.35499 3.18508 5.16832 3.33675 4.95832 3.33675Z" fill="#4B5563" />
                      <path d="M8.87252 13.2708H5.12752C3.09169 13.2708 3.01002 12.1449 2.94586 11.2349L2.56669 5.36075C2.54919 5.12159 2.73586 4.91159 2.97502 4.89409C3.22002 4.88242 3.42419 5.06326 3.44169 5.30242L3.82086 11.1766C3.88502 12.0633 3.90836 12.3958 5.12752 12.3958H8.87252C10.0975 12.3958 10.1209 12.0633 10.1792 11.1766L10.5584 5.30242C10.5759 5.06326 10.7859 4.88242 11.025 4.89409C11.2642 4.91159 11.4509 5.11576 11.4334 5.36075L11.0542 11.2349C10.99 12.1449 10.9084 13.2708 8.87252 13.2708Z" fill="#4B5563" />
                      <path d="M7.96832 10.0625H6.02582C5.78665 10.0625 5.58832 9.86417 5.58832 9.625C5.58832 9.38583 5.78665 9.1875 6.02582 9.1875H7.96832C8.20748 9.1875 8.40582 9.38583 8.40582 9.625C8.40582 9.86417 8.20748 10.0625 7.96832 10.0625Z" fill="#4B5563" />
                      <path d="M8.45835 7.72925H5.54169C5.30252 7.72925 5.10419 7.53091 5.10419 7.29175C5.10419 7.05258 5.30252 6.85425 5.54169 6.85425H8.45835C8.69752 6.85425 8.89585 7.05258 8.89585 7.29175C8.89585 7.53091 8.69752 7.72925 8.45835 7.72925Z" fill="#4B5563" />
                    </svg>
                  </button>
                </Tooltip>
                <Controls />
                <Tooltip title="Expande">
                  <button className='arrow-expand' size="small" onClick={handleClickArrowBottom}>
                    <svg width="6" height="11" viewBox="0 0 6 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M0.3125 4.52539C-0.00427618 4.8422 -0.00429678 5.36777 0.3125 5.68457L4.52539 9.89746C4.8422 10.2142 5.36779 10.2142 5.68457 9.89746C5.84878 9.73325 5.9248 9.52532 5.9248 9.31738C5.92471 9.10961 5.84862 8.90238 5.68457 8.73828L2.05078 5.10449L2.10449 5.05176L5.68457 1.47168C6.00134 1.15488 6.00134 0.6293 5.68457 0.3125C5.36777 -0.0042967 4.8422 -0.00427595 4.52539 0.3125L0.3125 4.52539Z" fill="#4B5563" stroke="white" stroke-width="0.15" />
                    </svg>
                  </button>
                </Tooltip>
              </div>
            </Panel>
          </ReactFlow>
        </Box>

        {/* Node Configuration Dialog */}
        <NodeConfigDialog
          open={configDialogOpen}
          onClose={() => setConfigDialogOpen(false)}
          node={selectedNode}
          onSave={handleNodeConfigSave}
          nodes={nodes}
          edges={edges}
          workflowId={workflowId}
          nodeTemplates={nodeTemplates}
          nodeTools={nodeTools}
          onNavigatePrevious={handlePreviousNode}
          onNavigateNext={handleNextNode}
        />
      </Box>
    </ReactFlowProvider>
  );
};

// Main component with ReactFlowProvider
const ReactFlowWorkflowBuilder = (props) => {
  return <ReactFlowProvider> <WorkflowBuilderContent {...props} /> </ReactFlowProvider>
}
export default ReactFlowWorkflowBuilder;